import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Button from '@/components/common/Button';
import RichTextArea from '@/components/common/RichTextArea';
import Avatar from '@/components/common/Avatar';
import { TaskPriority, TaskStatus, WorkItemType } from '@/types';
import { useHierarchy } from '@/contexts/HierarchyContext';
import { hasRichTextContent, plainTextToRichHtml, stripHtmlForAi } from '@/utils/richTextAi';
import FullScreenWorkItemCreator from './FullScreenWorkItemCreator';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import {
  useAddCommentMutation,
  useCreateTimeEntryMutation,
  useGetTaskByIdQuery,
  useGetTaskCommentsQuery,
  useGetTimeEntriesQuery,
  useGetTaskHistoryQuery,
  useUpdateTaskMutation,
  useGetCustomFieldsQuery,
  useGetTaskCustomFieldValuesQuery,
  useSetTaskCustomFieldValuesMutation,
  useAiRewriteTextMutation,
  useAiExpandBulletsMutation,
  useAiGenerateDescriptionMutation,
  useAiSummarizeCommentsMutation,
  useUploadTaskAttachmentsMutation,
  useDeleteTaskAttachmentMutation,
  useDeleteTaskMutation,
  useDeleteTimeEntryMutation,
  useGetUsersQuery,
  useGetTaskWatchersQuery,
  useToggleTaskWatchMutation,
  useGetDependenciesQuery,
  useCreateDependencyMutation,
  useDeleteDependencyMutation,
  useGetTasksQuery,
  useGetProjectsQuery,
} from '@/store/api/apiSlice';
import ActivityTimeline from '@/components/ActivityTimeline';
import SearchableDropdown from '@/components/common/SearchableDropdown';
import AttachmentPreviewModal from '@/components/common/AttachmentPreviewModal';
import EditTimeEntryModal from '@/components/timesheet/EditTimeEntryModal';
import { resolveAttachmentLabel } from '@/utils/attachmentPreview';
import { toast } from 'react-toastify';
import { getUserDisplayName } from '@/utils/user';
import { taskIsInSprint, TIME_LOG_SPRINT_REQUIRED_MESSAGE } from '@/utils/taskTime';
import { getBrowserTimeZone, toLocalDateInput, getTodayLocalDateInput } from '@/utils/dateTime';
import { useUserRegionalPrefs } from '@/hooks/useUserRegionalPrefs';
import {
  FiZap, FiPaperclip, FiFile, FiX, FiTrash2, FiUpload, FiEdit2,
  FiTarget, FiFlag, FiUsers, FiCode, FiAlertCircle, FiCheckCircle, FiPlus,
  FiClock, FiTag, FiMessageSquare, FiGitBranch, FiMaximize2, FiMinimize2,
  FiCopy, FiChevronLeft, FiChevronRight, FiChevronDown, FiSearch, FiEye, FiEyeOff, FiMoreVertical,
  FiArrowUp, FiArrowDown, FiSave,
} from 'react-icons/fi';
import { actionBtnIconClass, actionBtnOutline, actionBtnPrimary } from '@/utils/actionButtonClasses';

/* ================================================================
   Types & Constants
   ================================================================ */

import {
  TASK_ATTACHMENT_ACCEPT,
  getMaxAttachmentBytesForPlan,
  formatAttachmentMaxLabel,
} from '@/utils/taskAttachments';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { getApiError } from '@/utils/errorUtils';
import { getAiError } from '@/utils/aiErrors';
import { getWorkItemUrl, type TaskLookupArg } from '@/utils/workItemPath';

const TASK_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Plural labels for hierarchy panels (avoid "User Storys"). */
function pluralizeWorkItemType(type: WorkItemType, translate: (key: string) => string): string {
  switch (type) {
    case WorkItemType.USER_STORY:
      return translate('taskDetails.childTypes.USER_STORY');
    case WorkItemType.FEATURE:
      return translate('taskDetails.childTypes.FEATURE');
    case WorkItemType.TASK:
      return translate('taskDetails.childTypes.TASK');
    case WorkItemType.BUG:
      return translate('taskDetails.childTypes.BUG');
    case WorkItemType.EPIC:
      return translate('taskDetails.childTypes.EPIC');
    default:
      return translate('taskDetails.childItems');
  }
}

function workItemTypeLabel(type: WorkItemType, translate: (key: string, options?: { defaultValue?: string }) => string): string {
  return translate(`workItem.types.${type}.label`, {
    defaultValue: type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  });
}

interface TaskDetailsModalProps {
  taskId?: string;
  /** Slug from permalink route (e.g. userstory) when opening by sequence number. */
  workItemTypeSlug?: string;
  /** Pre-built lookup for getTaskById (permalink). */
  taskLookupArg?: TaskLookupArg;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

interface TaskFormState {
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigneeId: string;
  dueDate: string;
  estimatedHours: string;
  tags: string;
  businessValue: string;
  targetRelease: string;
  riskAssessment: string;
  dependencies: string;
  persona: string;
  storyPoints: string;
  userNotes: string;
  acceptanceCriteria: string;
  stepsToReproduce: string;
  expectedBehavior: string;
  actualBehavior: string;
  environment: string;
  severity: string;
  technicalDetails: string;
  complexity: string;
  blockers: string;
  epicRisk: string;
  epicValueArea: string;
  epicTimeCriticality: string;
  epicEffort: string;
}

const defaultState: TaskFormState = {
  title: '',
  description: '',
  status: TaskStatus.TODO,
  priority: TaskPriority.MEDIUM,
  assigneeId: '',
  dueDate: '',
  estimatedHours: '',
  tags: '',
  businessValue: '',
  targetRelease: '',
  riskAssessment: '',
  dependencies: '',
  persona: '',
  storyPoints: '',
  userNotes: '',
  acceptanceCriteria: '',
  stepsToReproduce: '',
  expectedBehavior: '',
  actualBehavior: '',
  environment: '',
  severity: '',
  technicalDetails: '',
  complexity: '',
  blockers: '',
  epicRisk: '',
  epicValueArea: '',
  epicTimeCriticality: '',
  epicEffort: '',
};

const toDateInput = (value?: string) => {
  return toLocalDateInput(value);
};

const STATUS_COLORS: Record<string, string> = {
  [TaskStatus.TODO]: 'bg-gray-400',
  [TaskStatus.IN_PROGRESS]: 'bg-blue-500',
  [TaskStatus.IN_REVIEW]: 'bg-orange-500',
  [TaskStatus.READY_FOR_TESTING]: 'bg-yellow-400',
  [TaskStatus.NEEDS_ATTENTION]: 'bg-amber-500',
  [TaskStatus.DONE]: 'bg-green-500',
  [TaskStatus.BLOCKED]: 'bg-red-500',
};

const PRIORITY_LABELS: Record<string, string> = {
  [TaskPriority.URGENT]: '1 - Critical',
  [TaskPriority.HIGH]: '2 - High',
  [TaskPriority.MEDIUM]: '3 - Medium',
  [TaskPriority.LOW]: '4 - Low',
};

const TYPE_BADGE: Record<string, { bg: string; text: string }> = {
  [WorkItemType.EPIC]: { bg: 'bg-orange-600', text: 'text-white' },
  [WorkItemType.FEATURE]: { bg: 'bg-purple-700', text: 'text-white' },
  [WorkItemType.USER_STORY]: { bg: 'bg-blue-600', text: 'text-white' },
  [WorkItemType.TASK]: { bg: 'bg-yellow-500', text: 'text-text-primary' },
  [WorkItemType.BUG]: { bg: 'bg-red-600', text: 'text-white' },
};

const TYPE_CHIP: Record<string, { bg: string; text: string }> = {
  [WorkItemType.EPIC]: { bg: 'bg-orange-500', text: 'text-white' },
  [WorkItemType.FEATURE]: { bg: 'bg-purple-600', text: 'text-white' },
  // Was bg-brand-subtle0 (invalid class) + text-white → invisible User Story icons in child lists
  [WorkItemType.USER_STORY]: { bg: 'bg-blue-600', text: 'text-white' },
  [WorkItemType.TASK]: { bg: 'bg-yellow-400', text: 'text-text-primary' },
  [WorkItemType.BUG]: { bg: 'bg-red-500', text: 'text-white' },
};

/* ================================================================
   Component
   ================================================================ */

export default function TaskDetailsModal({
  taskId,
  workItemTypeSlug,
  taskLookupArg,
  isOpen,
  onClose,
  onSaved,
}: TaskDetailsModalProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { formatDateShort } = useUserRegionalPrefs();
  const { parentTypeFor, isAllowed, childTypesFor } = useHierarchy();
  const { planInfo } = useFeatureAccess();
  const maxAttachmentBytes = useMemo(
    () => getMaxAttachmentBytesForPlan(planInfo?.usageTier, planInfo?.limits),
    [planInfo?.usageTier, planInfo?.limits],
  );
  const maxAttachmentLabel = useMemo(
    () => formatAttachmentMaxLabel(maxAttachmentBytes),
    [maxAttachmentBytes],
  );
  const browserTimeZone = useMemo(() => getBrowserTimeZone(), []);
  const [activeTaskId, setActiveTaskId] = useState<string | undefined>(taskId);
  const [taskHistory, setTaskHistory] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setTaskHistory([]);
      return;
    }
    setActiveTaskId(taskId);
    setTaskHistory([]);
  }, [taskId, isOpen]);

  /* ---------- data hooks ---------- */
  const initialLookupArg = useMemo((): TaskLookupArg | undefined => {
    if (taskLookupArg) return taskLookupArg;
    if (!activeTaskId) return undefined;
    if (workItemTypeSlug && /^\d+$/.test(activeTaskId)) {
      return { ref: activeTaskId, type: workItemTypeSlug };
    }
    return activeTaskId;
  }, [taskLookupArg, activeTaskId, workItemTypeSlug]);

  const { data: task, isFetching, refetch: refetchTask } = useGetTaskByIdQuery(initialLookupArg!, {
    skip: !initialLookupArg || !isOpen,
  });

  const resolvedTaskId = task?.id ?? (activeTaskId && TASK_UUID_RE.test(activeTaskId) ? activeTaskId : undefined);

  useEffect(() => {
    if (!task?.id || !isOpen) return;
    if (activeTaskId && /^\d+$/.test(activeTaskId)) {
      setActiveTaskId(task.id);
    }
  }, [task?.id, isOpen, activeTaskId]);

  const { data: comments = [], refetch: refetchComments } = useGetTaskCommentsQuery(resolvedTaskId as string, {
    skip: !resolvedTaskId || !isOpen,
  });
  const { data: timeEntries = [], refetch: refetchTimeEntries } = useGetTimeEntriesQuery(
    { taskId: resolvedTaskId, limit: 500 },
    { skip: !resolvedTaskId || !isOpen },
  );
  const { data: taskHistoryData, isLoading: isLoadingHistory } = useGetTaskHistoryQuery(
    { taskId: resolvedTaskId as string, limit: 50, offset: 0 },
    { skip: !resolvedTaskId || !isOpen },
  );
  const taskHistoryEntries = taskHistoryData?.history || [];
  const { data: users = [] } = useGetUsersQuery({ limit: 100 }, { skip: !isOpen });

  // Eligible parent type derived from the tenant-configured hierarchy mode.
  // Returns null for the top-level type (which has no parent in the active mode).
  const eligibleParentType = task?.type ? parentTypeFor(task.type as WorkItemType) : null;
  const { data: eligibleParentsData } = useGetTasksQuery(
    { projectId: task?.projectId, type: eligibleParentType, limit: 100 },
    { skip: !isOpen || !task?.projectId || !eligibleParentType }
  );
  const eligibleParents: any[] = (eligibleParentsData as any)?.tasks || (Array.isArray(eligibleParentsData) ? eligibleParentsData : []);

  const { data: projectsData } = useGetProjectsQuery({}, { skip: !isOpen });
  const allProjects: any[] = (projectsData as any)?.projects || (Array.isArray(projectsData) ? projectsData : []);

  const [updateTask, { isLoading: isSaving }] = useUpdateTaskMutation();
  const [addComment, { isLoading: isAddingComment }] = useAddCommentMutation();
  const [createTimeEntry, { isLoading: isLoggingTime }] = useCreateTimeEntryMutation();
  const [uploadAttachments, { isLoading: isUploading }] = useUploadTaskAttachmentsMutation();
  const [deleteAttachment, { isLoading: isDeleting }] = useDeleteTaskAttachmentMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [deleteTimeEntry] = useDeleteTimeEntryMutation();
  const { data: watchersData, refetch: refetchWatchers } = useGetTaskWatchersQuery(resolvedTaskId as string, {
    skip: !resolvedTaskId || !isOpen,
  });
  const [toggleWatch, { isLoading: isTogglingWatch }] = useToggleTaskWatchMutation();

  const [aiRewriteText, { isLoading: isRewriting }] = useAiRewriteTextMutation();
  const [aiExpandBullets, { isLoading: isExpanding }] = useAiExpandBulletsMutation();
  const [aiGenerateDescription, { isLoading: isGeneratingDesc }] = useAiGenerateDescriptionMutation();
  const [aiSummarizeComments] = useAiSummarizeCommentsMutation();

  const { data: customFields = [] } = useGetCustomFieldsQuery({ projectId: task?.projectId }, { skip: !task?.projectId || !isOpen });
  const { data: customFieldValues = [] } = useGetTaskCustomFieldValuesQuery(resolvedTaskId as string, {
    skip: !resolvedTaskId || !isOpen,
  });
  const [setTaskCustomFieldValues] = useSetTaskCustomFieldValuesMutation();

  /* ---------- Dependency hooks ---------- */
  const { data: taskDependencies = [] } = useGetDependenciesQuery(resolvedTaskId as string, {
    skip: !resolvedTaskId || !isOpen,
  });
  const [createDependency, { isLoading: isCreatingDep }] = useCreateDependencyMutation();
  const [deleteDependency, { isLoading: isDeletingDep }] = useDeleteDependencyMutation();

  /* ---------- Authorization check ---------- */
  const userRole = user?.role;
  const userId = user?.id;
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'MANAGER';
  const isAssignee = task?.assigneeId === userId;
  const canEdit = true; // all team members can edit task details
  const canLogTimeByRole = isAdmin || isAssignee;
  const canLogTime = canLogTimeByRole && task ? taskIsInSprint(task) : false;

  /* ---------- Watcher state ---------- */
  const watchers: { id: string; firstName: string; lastName: string; avatar?: string; email: string }[] = watchersData?.watchers ?? [];
  const isWatching: boolean = watchersData?.isWatching ?? false;

  const handleToggleWatch = async () => {
    if (!activeTaskId) return;
    try {
      await toggleWatch(activeTaskId).unwrap();
      refetchWatchers();
    } catch {
      toast.error(t('taskDetails.toasts.watchFailed'));
    }
  };

  /* ---------- local state ---------- */
  const [form, setForm] = useState<TaskFormState>(defaultState);
  const [commentText, setCommentText] = useState('');
  const [depTargetSearch, setDepTargetSearch] = useState('');
  const [depType, setDepType] = useState('BLOCKS');
  const [depSearchOpen, setDepSearchOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null); // null = no active mention
  const [mentionDropdownUsers, setMentionDropdownUsers] = useState<any[]>([]);
  const commentInputRef = useRef<HTMLInputElement>(null);
  const [hoursToLog, setHoursToLog] = useState('1');
  const [timeDescription, setTimeDescription] = useState('');
  const [editTimeEntry, setEditTimeEntry] = useState<any | null>(null);
  const [logForUserId, setLogForUserId] = useState<string>('');
  const [timeLogDate, setTimeLogDate] = useState(() => getTodayLocalDateInput());
  const [cfValues, setCfValues] = useState<Record<string, string>>({});
  const [showAiMenu, setShowAiMenu] = useState(false);
  const [showAcMenu, setShowAcMenu] = useState(false);
  const [commentSummary, setCommentSummary] = useState<{ summary: string; keyPoints: string[] } | null>(null);
  const [isSummarizingComments, setIsSummarizingComments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showCreator, setShowCreator] = useState(false);
  const [creatorType, setCreatorType] = useState<WorkItemType | null>(null);
  const [addChildMenuOpen, setAddChildMenuOpen] = useState(false);
  const [addChildToolbarMenuOpen, setAddChildToolbarMenuOpen] = useState(false);
  const addChildMenuRef = useRef<HTMLDivElement>(null);
  const addChildToolbarMenuRef = useRef<HTMLDivElement>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{ url: string; name: string } | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [panelWidth, setPanelWidth] = useState(60); // vw percentage
  const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const assigneeInputRef = useRef<HTMLInputElement>(null);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);

  /* ---------- Parent picker ---------- */
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [parentSearch, setParentSearch] = useState('');
  const parentPickerRef = useRef<HTMLDivElement>(null);

  /* ---------- Move to project ---------- */
  const [moveProjectOpen, setMoveProjectOpen] = useState(false);
  const moveProjectRef = useRef<HTMLDivElement>(null);

  /* ---------- 3-dot more actions menu ---------- */
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [changeTypeMenuOpen, setChangeTypeMenuOpen] = useState(false);
  const [assignParentMenuOpen, setAssignParentMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  /* ---------- Panel width resizing ---------- */
  const panelWidthRef = useRef<number>(60); // Store width as vw percentage
  const isResizingRef = useRef(false);
  const resizeDragStartX = useRef(0);

  /* ---------- 3-column resizable panes ---------- */
  const [colPcts, setColPcts] = useState([50, 20, 30]);
  /** Col 2 (child items) always starts collapsed — open via sidebar rail or "View all". */
  const [colVisible, setColVisible] = useState([true, true, false]);
  const containerRef = useRef<HTMLDivElement>(null);
  const descriptionScrollRef = useRef<HTMLDivElement>(null);
  const draggingDivider = useRef<number | null>(null);
  const dragStartX = useRef(0);
  const dragStartPcts = useRef([50, 20, 30]);

  /** Collapse attachments panel (list + upload) behind a single header row */
  const [descAttachmentsOpen, setDescAttachmentsOpen] = useState(true);
  const [propsAttachmentsOpen, setPropsAttachmentsOpen] = useState(true);

  /* ---------- Keep child column collapsed when opening a work item ---------- */
  useEffect(() => {
    if (!task?.id) return;
    // Always start with children panel hidden — open via sidebar rail or "View all"
    setColVisible([true, true, false]);
  }, [task?.id]);

  const toggleColumn = (index: number) => {
    setColVisible(prev => {
      const next = [...prev];
      const visCount = next.filter(Boolean).length;
      if (next[index] && visCount <= 1) return prev;
      next[index] = !next[index];
      return next;
    });
  };

  const [descScrollFabToTop, setDescScrollFabToTop] = useState(false);
  const [descScrollHasOverflow, setDescScrollHasOverflow] = useState(false);

  const syncDescriptionScrollFab = useCallback(() => {
    const el = descriptionScrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflow = scrollHeight > clientHeight + 6;
    setDescScrollHasOverflow(overflow);
    if (!overflow) {
      setDescScrollFabToTop(false);
      return;
    }
    const atBottom = scrollTop + clientHeight >= scrollHeight - 40;
    setDescScrollFabToTop(atBottom);
  }, []);

  const handleDividerMouseDown = useCallback((dividerIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    if (!containerRef.current) return;
    draggingDivider.current = dividerIndex;
    dragStartX.current = e.clientX;
    dragStartPcts.current = [...colPcts];
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      if (draggingDivider.current === null || !containerRef.current) return;
      const containerWidth = containerRef.current.getBoundingClientRect().width;
      const deltaPx = ev.clientX - dragStartX.current;
      const deltaPct = (deltaPx / containerWidth) * 100;
      const idx = draggingDivider.current;
      const newPcts = [...dragStartPcts.current];
      newPcts[idx] = Math.max(10, dragStartPcts.current[idx] + deltaPct);
      newPcts[idx + 1] = Math.max(10, dragStartPcts.current[idx + 1] - deltaPct);
      const total = newPcts.reduce((a, b) => a + b, 0);
      setColPcts(newPcts.map(p => (p / total) * 100));
    };

    const onUp = () => {
      draggingDivider.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colPcts]);

  useEffect(() => {
    if (!isOpen || !task) return;
    const t = requestAnimationFrame(() => syncDescriptionScrollFab());
    return () => cancelAnimationFrame(t);
  }, [isOpen, task?.id, syncDescriptionScrollFab]);

  useEffect(() => {
    const el = descriptionScrollRef.current;
    if (!el || !isOpen) return;
    const ro = new ResizeObserver(() => syncDescriptionScrollFab());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, task?.id, syncDescriptionScrollFab]);

  // Close assignee dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(e.target as Node) &&
        assigneeInputRef.current &&
        !assigneeInputRef.current.contains(e.target as Node)
      ) {
        setAssigneeMenuOpen(false);
      }
    };
    
    if (assigneeMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [assigneeMenuOpen]);

  // Close parent picker and project mover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (parentPickerRef.current && !parentPickerRef.current.contains(e.target as Node)) {
        setParentPickerOpen(false);
      }
      if (moveProjectRef.current && !moveProjectRef.current.contains(e.target as Node)) {
        setMoveProjectOpen(false);
      }
    };
    if (parentPickerOpen || moveProjectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [parentPickerOpen, moveProjectOpen]);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setMoreMenuOpen(false);
        setChangeTypeMenuOpen(false);
        setAssignParentMenuOpen(false);
        setShowAiMenu(false);
        setShowAcMenu(false);
      }
    };
    if (moreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreMenuOpen]);

  // Close Add child dropdowns when clicking outside
  useEffect(() => {
    if (!addChildMenuOpen && !addChildToolbarMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (addChildToolbarMenuOpen && addChildToolbarMenuRef.current && !addChildToolbarMenuRef.current.contains(target)) {
        setAddChildToolbarMenuOpen(false);
      }
      if (addChildMenuOpen && addChildMenuRef.current && !addChildMenuRef.current.contains(target)) {
        setAddChildMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [addChildMenuOpen, addChildToolbarMenuOpen]);

  /* ---------- sync form from server ---------- */
  useEffect(() => {
    if (!task || !isOpen) return;
    const metadata = (task as any).metadata || {};
    setForm({
      title: task.title || '',
      description: task.description || '',
      status: task.status || TaskStatus.TODO,
      priority: task.priority || TaskPriority.MEDIUM,
      assigneeId: task.assigneeId || '',
      dueDate: toDateInput(task.dueDate),
      estimatedHours: task.estimatedHours == null ? '' : String(task.estimatedHours),
      tags: Array.isArray(task.tags) ? task.tags.join(', ') : '',
      businessValue: metadata.businessValue || '',
      targetRelease: metadata.targetRelease || '',
      riskAssessment: metadata.riskLevel || metadata.riskAssessment || '',
      dependencies: metadata.dependencies || '',
      persona: metadata.userPersona || metadata.persona || '',
      storyPoints: metadata.storyPoints || '',
      userNotes: metadata.userNotes || '',
      acceptanceCriteria: task.acceptanceCriteria || metadata.acceptanceCriteria || '',
      stepsToReproduce: metadata.stepsToReproduce || '',
      expectedBehavior: metadata.expectedBehavior || '',
      actualBehavior: metadata.actualBehavior || '',
      environment: metadata.environment || '',
      severity: metadata.severity || '',
      technicalDetails: metadata.technicalDetails || '',
      complexity: metadata.complexity || '',
      blockers: metadata.blockers || '',
      epicRisk: metadata.risk || '',
      epicValueArea: metadata.valueArea || '',
      epicTimeCriticality: metadata.timeCriticality != null ? String(metadata.timeCriticality) : '',
      epicEffort: metadata.effort != null ? String(metadata.effort) : '',
    });
  }, [task, isOpen]);

  useEffect(() => {
    if (!customFieldValues.length) return;
    const vals: Record<string, string> = {};
    customFieldValues.forEach((cfv: any) => { vals[cfv.customFieldId] = cfv.value ?? ''; });
    setCfValues(vals);
  }, [customFieldValues]);

  useEffect(() => {
    if (!isOpen || !activeTaskId) return;
    setTimeLogDate(getTodayLocalDateInput());
    setHoursToLog('0');
    setTimeDescription('');
    setLogForUserId('');
  }, [isOpen, activeTaskId]);

  /* ---------- dirty check ---------- */
  const isDirty = useMemo(() => {
    if (!task) return false;
    const m = (task as any).metadata || {};
    const normalizedTaskAssigneeId = task.assigneeId || '';
    const normalizedFormAssigneeId = form.assigneeId || '';
    return (
      form.title !== (task.title || '') ||
      form.description !== (task.description || '') ||
      form.status !== task.status ||
      form.priority !== task.priority ||
      normalizedFormAssigneeId !== normalizedTaskAssigneeId ||
      form.dueDate !== toDateInput(task.dueDate) ||
      form.estimatedHours !== (task.estimatedHours == null ? '' : String(task.estimatedHours)) ||
      form.tags !== (Array.isArray(task.tags) ? task.tags.join(', ') : '') ||
      form.businessValue !== (m.businessValue || '') ||
      form.targetRelease !== (m.targetRelease || '') ||
      form.riskAssessment !== (m.riskLevel || m.riskAssessment || '') ||
      form.dependencies !== (m.dependencies || '') ||
      form.persona !== (m.userPersona || m.persona || '') ||
      form.storyPoints !== (m.storyPoints || '') ||
      form.userNotes !== (m.userNotes || '') ||
      form.acceptanceCriteria !== (task.acceptanceCriteria || m.acceptanceCriteria || '') ||
      form.stepsToReproduce !== (m.stepsToReproduce || '') ||
      form.expectedBehavior !== (m.expectedBehavior || '') ||
      form.actualBehavior !== (m.actualBehavior || '') ||
      form.environment !== (m.environment || '') ||
      form.severity !== (m.severity || '') ||
      form.technicalDetails !== (m.technicalDetails || '') ||
      form.complexity !== (m.complexity || '') ||
      form.blockers !== (m.blockers || '') ||
      form.epicRisk !== (m.risk || '') ||
      form.epicValueArea !== (m.valueArea || '') ||
      form.epicTimeCriticality !== (m.timeCriticality != null ? String(m.timeCriticality) : '') ||
      form.epicEffort !== (m.effort != null ? String(m.effort) : '')
    );
  }, [form, task]);

  /* ================================================================
     Handlers
     ================================================================ */

  const handleSave = async (closeAfterSave = false) => {
    if (!activeTaskId) return;
    if (!form.title.trim()) { toast.error(t('taskDetails.toasts.titleRequired')); return; }
    if (!form.description.trim()) { toast.error(t('taskDetails.toasts.descriptionRequired')); return; }
    if (task?.type === WorkItemType.USER_STORY && !form.storyPoints.trim()) {
      toast.error(t('taskDetails.toasts.storyPointsRequired'));
      return;
    }

    console.log('💾 Saving task...', { taskId: activeTaskId, assigneeId: form.assigneeId, status: form.status });

    // Build metadata object without null values
    let metadata: any = {};
    if (task?.type === 'EPIC') {
      if (form.businessValue.trim()) metadata.businessValue = form.businessValue.trim();
      if (form.epicRisk.trim()) metadata.risk = form.epicRisk.trim();
      if (form.epicValueArea.trim()) metadata.valueArea = form.epicValueArea.trim();
      if (form.epicTimeCriticality.trim()) metadata.timeCriticality = Number(form.epicTimeCriticality.trim());
      if (form.epicEffort.trim()) metadata.effort = Number(form.epicEffort.trim());
    } else if (task?.type === 'FEATURE') {
      if (form.businessValue.trim()) metadata.businessValue = form.businessValue.trim();
      if (form.targetRelease.trim()) metadata.targetRelease = form.targetRelease.trim();
      if (form.riskAssessment.trim()) metadata.riskLevel = form.riskAssessment.trim();
      if (form.dependencies.trim()) metadata.dependencies = form.dependencies.trim();
    } else if (task?.type === 'USER_STORY') {
      if (form.persona.trim()) metadata.userPersona = form.persona.trim();
      if (form.storyPoints.trim()) metadata.storyPoints = form.storyPoints.trim();
      if (form.userNotes.trim()) metadata.userNotes = form.userNotes.trim();
    } else if (task?.type === 'TASK') {
      if (form.technicalDetails.trim()) metadata.technicalDetails = form.technicalDetails.trim();
      if (form.complexity.trim()) metadata.complexity = form.complexity.trim();
      if (form.blockers.trim()) metadata.blockers = form.blockers.trim();
    } else if (task?.type === 'BUG') {
      if (form.stepsToReproduce.trim()) metadata.stepsToReproduce = form.stepsToReproduce.trim();
      if (form.expectedBehavior.trim()) metadata.expectedBehavior = form.expectedBehavior.trim();
      if (form.actualBehavior.trim()) metadata.actualBehavior = form.actualBehavior.trim();
      if (form.environment.trim()) metadata.environment = form.environment.trim();
      if (form.severity.trim()) metadata.severity = form.severity.trim();
    }

    const payload: any = {
      id: activeTaskId,
      title: form.title.trim(),
      description: form.description.trim(),
      status: form.status,
      priority: form.priority,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    };

    // Only include optional fields if they have values
    if (form.assigneeId) payload.assigneeId = form.assigneeId;
    if (form.dueDate) payload.dueDate = new Date(form.dueDate).toISOString();
    if (form.estimatedHours.trim() !== '') {
      const hours = Number(form.estimatedHours);
      if (Number.isNaN(hours) || hours < 0) {
        toast.error(t('taskDetails.toasts.estimatedHoursInvalid'));
        return;
      }
      payload.estimatedHours = hours;
    }
    if (form.acceptanceCriteria.trim()) payload.acceptanceCriteria = form.acceptanceCriteria.trim();
    if (Object.keys(metadata).length > 0) payload.metadata = metadata;

    console.log('📤 Payload:', payload);

    try {
      const result = await updateTask(payload).unwrap();
      console.log('✅ Task updated:', result);
      await refetchTask();
      if (customFields.length > 0) {
        const values = Object.entries(cfValues).filter(([, v]) => v !== '').map(([customFieldId, value]) => ({ customFieldId, value }));
        if (values.length > 0) await setTaskCustomFieldValues({ taskId: activeTaskId, values }).unwrap();
      }
      toast.success(t('taskDetails.toasts.saved'), { autoClose: 2000 });
      if (onSaved) onSaved();
      if (closeAfterSave) onClose();
    } catch (error: any) {
      console.error('❌ Update failed:', error);
      console.error('❌ Error data:', error?.data);
      console.error('❌ Error errors:', error?.data?.errors);
      const errorMsg = error?.data?.errors 
        ? Object.entries(error.data.errors).map(([field, msgs]: [string, any]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`).join('; ')
        : error?.data?.message || error?.message || t('taskDetails.toasts.updateFailed');
      toast.error(errorMsg, { autoClose: 5000 });
    }
  };

  const filteredUsersForAssignee = useMemo(() => {
    const query = assigneeSearch.trim().toLowerCase();
    if (!query) return (users as any[]).slice(0, 10);
    
    return (users as any[])
      .filter((u: any) => {
        const firstName = (u.firstName || '').toLowerCase();
        const lastName = (u.lastName || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`;
        
        return fullName.includes(query) ||
               firstName.includes(query) ||
               lastName.includes(query) ||
               email.includes(query) ||
               email.split('@')[0].includes(query);
      })
      .slice(0, 20);
  }, [users, assigneeSearch]);

  const handleAssigneeChange = async (newAssigneeId: string) => {
    try {
      await updateTask({ id: activeTaskId, assigneeId: newAssigneeId || null }).unwrap();
      setForm(p => ({ ...p, assigneeId: newAssigneeId }));
      toast.success(t('taskDetails.toasts.assigneeUpdated'));
      setAssigneeMenuOpen(false);
      setAssigneeSearch('');
    } catch {
      toast.error(t('taskDetails.toasts.assigneeUpdateFailed'));
    }
  };

  /* ---------- Panel resize handlers ---------- */
  const handlePanelResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    resizeDragStartX.current = e.clientX;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizingRef.current) return;
      const delta = resizeDragStartX.current - moveEvent.clientX; // negative = drag right = expand
      const newWidth = panelWidthRef.current + (delta / window.innerWidth) * 100;
      // Constrain between 30vw and 90vw
      const constrainedWidth = Math.max(30, Math.min(90, newWidth));
      panelWidthRef.current = constrainedWidth;
      setPanelWidth(constrainedWidth);
      resizeDragStartX.current = moveEvent.clientX;
    };

    const handleMouseUp = () => {
      isResizingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setCommentText(val);
    // Detect @mention: find last @ before cursor
    const cursor = e.target.selectionStart ?? val.length;
    const textBeforeCursor = val.slice(0, cursor);
    const atIdx = textBeforeCursor.lastIndexOf('@');
    if (atIdx !== -1) {
      const query = textBeforeCursor.slice(atIdx + 1);
      // Only show dropdown if the query contains no spaces (we're mid-mention)
      if (!query.includes(' ') || query.length <= 20) {
        setMentionQuery(query);
        const q = query.toLowerCase();
        const filtered = (users as any[]).filter((u: any) => {
          const name = `${u.firstName} ${u.lastName}`.toLowerCase();
          return name.includes(q) || u.email?.toLowerCase().includes(q);
        }).slice(0, 8);
        setMentionDropdownUsers(filtered);
      } else {
        setMentionQuery(null);
      }
    } else {
      setMentionQuery(null);
    }
  };

  const handleMentionSelect = (u: any) => {
    const cursor = commentInputRef.current?.selectionStart ?? commentText.length;
    const textBeforeCursor = commentText.slice(0, cursor);
    const atIdx = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = commentText.slice(cursor);
    const newText = `${commentText.slice(0, atIdx)}@${u.firstName} ${u.lastName} ${textAfterCursor}`;
    setCommentText(newText);
    setMentionQuery(null);
    setMentionDropdownUsers([]);
    setTimeout(() => commentInputRef.current?.focus(), 0);
  };

  const handleAddComment = async () => {
    if (!activeTaskId || !commentText.trim()) return;
    try {
      await addComment({ taskId: activeTaskId, content: commentText.trim() }).unwrap();
      setCommentText('');
      await refetchComments();
      toast.success(t('taskDetails.toasts.commentAdded'));
    } catch (error: any) { toast.error(error?.data?.message || t('taskDetails.toasts.commentAddFailed')); }
  };

  const handleLogTime = async () => {
    if (!activeTaskId) return;
    const hrs = Number(hoursToLog);
    if (Number.isNaN(hrs) || hrs <= 0) { toast.error(t('taskDetails.toasts.hoursInvalid')); return; }
    const anchor = timeLogDate ? new Date(`${timeLogDate}T12:00:00`) : new Date();
    const start = new Date(anchor.getTime() - (hrs / 2) * 3600000);
    const end = new Date(anchor.getTime() + (hrs / 2) * 3600000);
    const forUserId = isAdmin && logForUserId && logForUserId !== userId ? logForUserId : undefined;
    try {
      await createTimeEntry({ taskId: activeTaskId, startTime: start.toISOString(), endTime: end.toISOString(), description: timeDescription.trim() || undefined, ...(forUserId ? { forUserId } : {}) }).unwrap();
      await refetchTimeEntries();
      setHoursToLog('1');
      setTimeDescription('');
      setTimeLogDate(getTodayLocalDateInput());
      toast.success(forUserId ? t('taskDetails.toasts.timeLoggedFor', { name: users.find((u: any) => u.id === forUserId)?.firstName || t('taskDetails.teamMember') }) : t('taskDetails.toasts.timeLogged'));
    } catch (error: any) { 
      console.error('Time logging error:', error);
      toast.error(error?.data?.message || error?.message || t('taskDetails.toasts.timeLogFailed'));
    }
  };

  const handleDeleteTimeEntry = async (entryId: string) => {
    if (!window.confirm(t('taskDetails.confirmDeleteTime'))) return;
    try {
      await deleteTimeEntry(entryId).unwrap();
      await refetchTimeEntries();
      toast.success(t('taskDetails.toasts.timeDeleted'));
    } catch (error: any) {
      toast.error(error?.data?.message || t('taskDetails.toasts.timeDeleteFailed'));
    }
  };

  const handleAiRewriteField = async (
    field: 'description' | 'acceptanceCriteria',
    style: 'shorter' | 'professional' | 'grammar' | 'expand' | 'smart',
    closeMenu: () => void,
  ) => {
    const raw = field === 'description' ? form.description : form.acceptanceCriteria;
    if (!hasRichTextContent(raw)) {
      toast.error(field === 'description' ? t('taskDetails.toasts.descriptionEmpty') : t('taskDetails.toasts.acceptanceCriteriaEmpty'));
      return;
    }
    const plain = stripHtmlForAi(raw);
    try {
      const result = await aiRewriteText({ text: plain, style }).unwrap();
      if (result.applied === false) {
        toast.error(t('taskDetails.toasts.aiRewriteFailed'));
        return;
      }
      const rewritten = plainTextToRichHtml(result.rewrittenText);
      setForm((prev) => ({ ...prev, [field]: rewritten }));
      closeMenu();
      toast.success(t('taskDetails.toasts.textUpdated'));
    } catch (error: any) {
      const { getAiError } = await import('@/utils/aiErrors');
      toast.error(getAiError(error, t('taskDetails.toasts.rewriteFailed')));
    }
  };

  const handleAiRewrite = (style: 'shorter' | 'professional' | 'grammar' | 'expand' | 'smart') =>
    handleAiRewriteField('description', style, () => setShowAiMenu(false));

  const handleExpandBullets = async () => {
    if (!form.description.trim()) { toast.error(t('taskDetails.toasts.descriptionEmpty')); return; }
    try {
      const result = await aiExpandBullets({ text: form.description }).unwrap();
      setForm((prev) => ({ ...prev, description: result.expandedText }));
      setShowAiMenu(false);
      toast.success(t('taskDetails.toasts.textExpanded'));
    } catch (error: any) { toast.error(getAiError(error, t('taskDetails.toasts.expandFailed'))); }
  };

  const handleGenerateDesc = async () => {
    if (!form.title.trim()) { toast.error(t('taskDetails.toasts.enterTitleFirst')); return; }
    try {
      const result = await aiGenerateDescription({ title: form.title }).unwrap();
      setForm((prev) => ({ ...prev, description: result.description }));
      setShowAiMenu(false);
      toast.success(t('taskDetails.toasts.descriptionGenerated'));
    } catch (error: any) { toast.error(getAiError(error, t('taskDetails.toasts.generateDescriptionFailed'))); }
  };

  const handleSummarizeComments = async () => {
    if (comments.length === 0) { toast.error(t('taskDetails.toasts.noCommentsToSummarize')); return; }
    setIsSummarizingComments(true);
    try {
      const formatted = comments.map((c: any) => ({ author: getUserDisplayName(c.user), text: c.content, createdAt: new Date(c.createdAt) }));
      const result = await aiSummarizeComments({ comments: formatted }).unwrap();
      setCommentSummary({ summary: result.summary, keyPoints: result.keyPoints });
      toast.success(t('taskDetails.toasts.commentsSummarized'));
    } catch (error: any) { toast.error(getAiError(error, t('taskDetails.toasts.summarizeFailed'))); }
    finally { setIsSummarizingComments(false); }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const valid = Array.from(files).filter(f => {
        if (f.size > maxAttachmentBytes) {
          toast.error(t('attachments.tooLarge', { name: f.name, limit: maxAttachmentLabel }));
          return false;
        }
        return true;
      });
      setSelectedFiles(prev => [...prev, ...valid]);
    }
  };

  const handleUploadFiles = async () => {
    if (!activeTaskId || selectedFiles.length === 0) return;
    try {
      await uploadAttachments({ taskId: activeTaskId, files: selectedFiles }).unwrap();
      toast.success(t('taskDetails.toasts.filesUploaded'));
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error: unknown) {
      toast.error(getApiError(error, t('taskDetails.toasts.uploadFailed')));
    }
  };

  const handleRemoveSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteAttachment = async (attachmentUrl: string) => {
    if (!activeTaskId) return;
    if (!confirm(t('taskDetails.confirmDeleteAttachment'))) return;
    try {
      await deleteAttachment({ taskId: activeTaskId, attachmentUrl }).unwrap();
      toast.success(t('taskDetails.toasts.attachmentDeleted'));
    } catch (error: any) { toast.error(error?.data?.message || t('taskDetails.toasts.attachmentDeleteFailed')); }
  };

  /* ---------- helpers ---------- */
  const getFileName = (url: string) => resolveAttachmentLabel(url.split('/').pop() || '', url);
  const getFileExtension = (name: string) => { const p = name.split('.'); return p.length > 1 ? p.pop()!.toLowerCase() : ''; };
  const getTypeIcon = (type?: string) => {
    const cls = 'w-3.5 h-3.5';
    switch (type) {
      case WorkItemType.EPIC: return <FiFlag className={cls} />;
      case WorkItemType.FEATURE: return <FiTarget className={cls} />;
      case WorkItemType.USER_STORY: return <FiUsers className={cls} />;
      case WorkItemType.TASK: return <FiCheckCircle className={cls} />;
      case WorkItemType.BUG: return <FiAlertCircle className={cls} />;
      default: return <FiFlag className={cls} />;
    }
  };

  const badge = TYPE_BADGE[task?.type as string] || { bg: 'bg-gray-600', text: 'text-white' };
  const chip = (type: string) => TYPE_CHIP[type] || { bg: 'bg-gray-400', text: 'text-white' };
  const allowedChildTypesForTask = task ? childTypesFor(task.type as WorkItemType) : [];
  const canHaveChildren = allowedChildTypesForTask.length > 0;
  const childLabel = allowedChildTypesForTask.length === 0
    ? t('taskDetails.childItems')
    : allowedChildTypesForTask.map((type) => pluralizeWorkItemType(type, t)).join(' / ');
  const childCount = task?.children?.length ?? 0;
  const openChildCreator = (childType?: WorkItemType) => {
    const type = childType ?? allowedChildTypesForTask[0];
    if (!type) return;
    setCreatorType(type);
    setShowCreator(true);
    // Reveal the side panel so the new hierarchy is visible after create
    setColVisible((prev) => (prev[2] ? prev : [prev[0], prev[1], true]));
  };
  const statusOptions = useMemo(
    () => Object.values(TaskStatus).map((s) => ({ value: s, label: t(`taskStatus.${s}`, { defaultValue: s }) })),
    [t],
  );
  const priorityOptions = useMemo(
    () => Object.values(TaskPriority).map((p) => ({ value: p, label: t(`workItemCreator.priority.${p}`, { defaultValue: PRIORITY_LABELS[p] || p }) })),
    [t],
  );

  const navigateToTask = (nextTaskId?: string) => {
    if (!nextTaskId || !activeTaskId || nextTaskId === activeTaskId) return;
    setTaskHistory((prev) => [...prev, activeTaskId]);
    setActiveTaskId(nextTaskId);
  };

  const handleParentChange = async (newParentId: string | null) => {
    if (!activeTaskId) return;
    try {
      await updateTask({ id: activeTaskId, parentId: newParentId }).unwrap();
      toast.success(newParentId ? t('taskDetails.toasts.parentAssigned') : t('taskDetails.toasts.parentRemoved'));
      setParentPickerOpen(false);
      setParentSearch('');
    } catch (err: any) {
      toast.error(err?.data?.error || t('taskDetails.toasts.parentUpdateFailed'));
    }
  };

  const handleMoveProject = async (newProjectId: string) => {
    if (!activeTaskId || newProjectId === task?.projectId) return;
    try {
      await updateTask({ id: activeTaskId, projectId: newProjectId }).unwrap();
      toast.success(t('taskDetails.toasts.projectMoved'));
      setMoveProjectOpen(false);
      setMoreMenuOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.error || t('taskDetails.toasts.projectMoveFailed'));
    }
  };

  const handleChangeType = async (newType: WorkItemType) => {
    if (!activeTaskId || newType === task?.type) return;
    try {
      // Always orphan the item on type change — parent type may no longer be valid
      await updateTask({ id: activeTaskId, type: newType, parentId: null }).unwrap();
      await refetchTask();
      toast.success(t('taskDetails.toasts.typeChanged'));
      setChangeTypeMenuOpen(false);
      setAssignParentMenuOpen(false);
      setMoreMenuOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.error || t('taskDetails.toasts.typeChangeFailed'));
    }
  };

  const handleDeleteTask = async () => {
    if (!activeTaskId) return;
    if (!window.confirm(t('taskDetails.confirmDeleteWorkItem'))) return;
    try {
      await deleteTask(activeTaskId).unwrap();
      toast.success(t('taskDetails.toasts.workItemDeleted'));
      setMoreMenuOpen(false);
      onClose();
    } catch (err: any) {
      toast.error(err?.data?.error || t('taskDetails.toasts.workItemDeleteFailed'));
    }
  };

  const handleBackNavigation = () => {
    setTaskHistory((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const previousTaskId = next.pop();
      if (previousTaskId) setActiveTaskId(previousTaskId);
      return next;
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success(t('taskDetails.linkCopied', { label })));
  };

  /* ================================================================
     Render
     ================================================================ */

  if (!isOpen) return null;

  const panelVwWidth = `${panelWidth}vw`;

  const portal = (
    <>
      {/* Backdrop - only covers right side panel area when not maximized */}
      {!isMaximized && (
        <div 
          className="fixed top-0 z-40 bg-black/20 cursor-pointer"
          onClick={onClose}
          style={{ 
                        right: 0,
            width: panelVwWidth,
            height: '100vh',
            pointerEvents: 'auto',
          }}
        />
      )}
      
      {/* Side Panel - slides from right to left */}
      <div
        className="fixed inset-y-0 right-0 z-50 bg-surface flex flex-col shadow-2xl overflow-hidden"
        style={{
                    width: isMaximized ? '100vw' : panelVwWidth,
          height: isMaximized ? '100vh' : '100vh',
          left: isMaximized ? 0 : 'auto',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out, width 0.2s ease-in-out',
          borderRadius: 0,
          willChange: 'transform',
        }}
      >
        {/* ── RESIZE HANDLE (left edge - invisible drag zone) ── */}
        {!isMaximized && (
          <div
            onMouseDown={handlePanelResizeStart}
            className="absolute top-0 left-0 h-full w-1.5 cursor-col-resize"
            style={{ userSelect: 'none', zIndex: 60 }}
            title={t('taskDetails.dragToResize')}
          />
        )}

        {/* ── LOADING ── */}
        {isFetching && !task ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-text-muted">{t('taskDetails.loading')}</span>
            </div>
          </div>
        ) : (
          <>
            {/* ============================================================
                HEADER BAR
                ============================================================ */}
            <div className="flex items-center justify-between px-5 py-2 border-b border-border bg-surface min-h-[40px] flex-shrink-0">
              <div className="flex items-center gap-3">
                {taskHistory.length > 0 && (
                  <button
                    onClick={handleBackNavigation}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand hover:bg-brand-subtle rounded transition-colors"
                    title={t('taskDetails.back')}
                  >
                    <FiChevronLeft className="w-3.5 h-3.5" />
                    Back
                  </button>
                )}
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-[3px] ${badge.bg} ${badge.text}`}>
                  {getTypeIcon(task?.type)}
                  <span className="text-xs font-semibold tracking-wide">
                    {task?.type
                      ? workItemTypeLabel(task.type as WorkItemType, t)
                      : t('taskDetails.item')}{task?.project?.key ? ` ${task.project.key}` : ''}-{task?.sequenceNumber ?? task?.id?.substring(0, 8)}
                  </span>
                </div>
                <button
                  onClick={() => task && copyToClipboard(getWorkItemUrl(task), t('taskDetails.copyLinkAction'))}
                  className="p-1 text-text-muted hover:text-text-primary hover:bg-surface-hover rounded transition-colors"
                  title={t('taskDetails.copyLink')}
                >
                  <FiCopy className="w-3.5 h-3.5" />
                </button>

                {/* 3-dot more actions */}
                <div className="relative" ref={moreMenuRef}>
                  <button
                    onClick={() => { setMoreMenuOpen(v => !v); setChangeTypeMenuOpen(false); }}
                    className="p-1 text-text-muted hover:text-text-primary hover:bg-surface-hover rounded transition-colors"
                    title={t('taskDetails.moreActions')}
                  >
                    <FiMoreVertical className="w-3.5 h-3.5" />
                  </button>

                  {moreMenuOpen && (
                    <div className="absolute left-0 top-full mt-1 w-52 bg-surface border border-border rounded-lg shadow-lg z-50 py-1 text-[13px]">
                      {/* Change type submenu */}
                      <div className="relative group">
                        <button
                          className="w-full text-left px-3 py-2 hover:bg-surface-hover flex items-center justify-between"
                          onClick={() => setChangeTypeMenuOpen(v => !v)}
                        >
                          <span>{t('taskDetails.changeType')}</span>
                          <FiChevronRight className="w-3.5 h-3.5 text-text-muted" />
                        </button>
                        {changeTypeMenuOpen && (
                          <div className="absolute left-full top-0 ml-1 w-44 bg-surface border border-border rounded-lg shadow-lg z-50 py-1">
                            {Object.values(WorkItemType).filter((type) => isAllowed(type)).map((type) => (
                              <button
                                key={type}
                                className={`w-full text-left px-3 py-2 hover:bg-surface-hover flex items-center gap-2 ${type === task?.type ? 'text-indigo-600 font-medium' : ''}`}
                                onClick={() => handleChangeType(type)}
                              >
                                {getTypeIcon(type)}
                                <span>{workItemTypeLabel(type, t)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Assign parent */}
                      {eligibleParentType !== null && (
                        <div className="relative">
                          <button
                            className="w-full text-left px-3 py-2 hover:bg-surface-hover flex items-center justify-between"
                            onClick={() => setAssignParentMenuOpen(v => !v)}
                          >
                            <span>{t('taskDetails.assignParent')}</span>
                            <FiChevronRight className="w-3.5 h-3.5 text-text-muted" />
                          </button>
                          {assignParentMenuOpen && (
                            <div className="absolute left-full top-0 ml-1 w-56 bg-surface border border-border rounded-lg shadow-lg z-50 py-1 max-h-60 overflow-y-auto">
                              {task?.parentId && (
                                <>
                                  <button
                                    className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 text-[12px] font-medium"
                                    onClick={async () => {
                                      try {
                                        await updateTask({ id: activeTaskId!, parentId: null }).unwrap();
                                        await refetchTask();
                                        toast.success(t('taskDetails.toasts.parentRemoved'));
                                      } catch (err: any) {
                                        toast.error(err?.data?.error || t('taskDetails.toasts.parentRemoveFailed'));
                                      }
                                      setAssignParentMenuOpen(false);
                                      setMoreMenuOpen(false);
                                    }}
                                  >
                                    ✕ Remove current parent
                                  </button>
                                  <div className="border-t border-border my-1" />
                                </>
                              )}
                              {eligibleParents.filter((p: any) => p.id !== activeTaskId).map((p: any) => (
                                <button
                                  key={p.id}
                                  className={`w-full text-left px-3 py-2 hover:bg-surface-hover text-[13px] ${p.id === task?.parentId ? 'text-indigo-600 font-medium' : ''}`}
                                  onClick={async () => {
                                    try {
                                      await updateTask({ id: activeTaskId!, parentId: p.id }).unwrap();
                                      await refetchTask();
                                      toast.success(t('taskDetails.toasts.parentAssigned'));
                                    } catch (err: any) {
                                      toast.error(err?.data?.error || t('taskDetails.toasts.parentAssignFailed'));
                                    }
                                    setAssignParentMenuOpen(false);
                                    setMoreMenuOpen(false);
                                  }}
                                >
                                  {p.id === task?.parentId ? '✓ ' : ''}{p.title}
                                </button>
                              ))}
                              {eligibleParents.filter((p: any) => p.id !== activeTaskId).length === 0 && (
                                <div className="px-3 py-2 text-text-muted text-xs">{t('taskDetails.noAvailableParents')}</div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Move to another project */}
                      <div className="relative" ref={moveProjectRef}>
                        <button
                          className="w-full text-left px-3 py-2 hover:bg-surface-hover flex items-center justify-between"
                          onClick={() => setMoveProjectOpen(v => !v)}
                        >
                          <span>{t('taskDetails.moveToTeamProject')}</span>
                          <FiChevronRight className="w-3.5 h-3.5 text-text-muted" />
                        </button>
                        {moveProjectOpen && (
                          <div className="absolute left-full top-0 ml-1 w-52 bg-surface border border-border rounded-lg shadow-lg z-50 py-1 max-h-60 overflow-y-auto">
                            {allProjects.filter(p => p.id !== task?.projectId).map((p) => (
                              <button
                                key={p.id}
                                className="w-full text-left px-3 py-2 hover:bg-surface-hover text-[13px]"
                                onClick={() => handleMoveProject(p.id)}
                              >
                                {p.name}
                              </button>
                            ))}
                            {allProjects.filter(p => p.id !== task?.projectId).length === 0 && (
                              <div className="px-3 py-2 text-text-muted text-xs">{t('taskDetails.noOtherProjects')}</div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="border-t border-border my-1" />

                      {/* Copy link */}
                      <button
                        className="w-full text-left px-3 py-2 hover:bg-surface-hover"
                        onClick={() => { if (task) copyToClipboard(getWorkItemUrl(task), t('taskDetails.copyLinkAction')); setMoreMenuOpen(false); }}
                      >
                        Copy link
                      </button>

                      <div className="border-t border-border my-1" />

                      {/* Delete */}
                      <button
                        className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600"
                        onClick={handleDeleteTask}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsMaximized(!isMaximized)}
                  className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-hover rounded transition-colors"
                  title={isMaximized ? t('taskDetails.restore') : t('taskDetails.maximize')}
                >
                  {isMaximized ? <FiMinimize2 className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-hover rounded transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>



            {/* ============================================================
                TITLE + TOOLBAR
                ============================================================ */}
            <div className="px-6 pt-3 pb-1.5 flex-shrink-0">
                <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-[13px] text-text-secondary">
                  {/* Assignee dropdown */}
                  <div className="flex items-center relative" data-no-row-click="true">
                    <div className="w-full flex items-center gap-2 px-2 py-1 border border-border rounded-md hover:border-border-strong transition-colors bg-surface">
                      {form.assigneeId && (users as any[]).find((u: any) => u.id === form.assigneeId) && (
                        <Avatar name={`${(users as any[]).find((u: any) => u.id === form.assigneeId)?.firstName} ${(users as any[]).find((u: any) => u.id === form.assigneeId)?.lastName}`} size="sm" />
                      )}
                      <input
                        ref={assigneeInputRef}
                        type="text"
                        value={assigneeSearch}
                        onChange={(e) => setAssigneeSearch(e.target.value)}
                        onFocus={() => canEdit && setAssigneeMenuOpen(true)}
                        placeholder={form.assigneeId && (users as any[]).find((u: any) => u.id === form.assigneeId) ? `${(users as any[]).find((u: any) => u.id === form.assigneeId)?.firstName} ${(users as any[]).find((u: any) => u.id === form.assigneeId)?.lastName}` : t('taskDetails.assigneePlaceholder')}
                        disabled={!canEdit}
                        className="flex-1 text-sm text-text-primary bg-transparent border-none focus:outline-none focus:ring-0 placeholder:text-text-muted disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    {assigneeMenuOpen && canEdit && (
                      <div className="absolute top-full left-0 mt-1 w-[250px] bg-surface border border-border rounded-lg shadow-xl z-[200] p-2" ref={assigneeDropdownRef}>
                        <div className="flex items-center justify-between mb-2 px-1">
                          <p className="text-xs font-semibold text-text-secondary">{t('taskDetails.assignUser')}</p>
                          <button
                            type="button"
                            onClick={() => {
                              setAssigneeMenuOpen(false);
                              setAssigneeSearch('');
                            }}
                            className="inline-flex items-center justify-center h-6 w-6 rounded border border-border text-text-muted hover:bg-slate-100"
                            title={t('common.close')}
                          >
                            <FiX className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        <div className="relative mb-2">
                          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                          <input
                            autoFocus
                            type="text"
                            value={assigneeSearch}
                            onChange={(e) => setAssigneeSearch(e.target.value)}
                            placeholder={t('taskDetails.searchEmail')}
                            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>

                        <div className="max-h-72 overflow-y-auto overscroll-contain">
                          <button
                            type="button"
                            onClick={() => handleAssigneeChange('')}
                            className="w-full text-left px-2 py-2 rounded hover:bg-surface-hover text-sm text-text-secondary"
                          >
                            Unassigned
                          </button>

                          {filteredUsersForAssignee.map((user: any) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => handleAssigneeChange(user.id)}
                              className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-surface-hover text-left"
                            >
                              <Avatar name={`${user.firstName} ${user.lastName}`} />
                              <div className="min-w-0">
                                <div className="text-sm text-text-primary truncate">{user.firstName} {user.lastName}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="text-gray-300">|</span>
                  <div className="flex items-center gap-1 text-text-muted">
                    <FiMessageSquare className="w-3.5 h-3.5" />
                    <span>{comments.length} comment{comments.length !== 1 ? 's' : ''}</span>
                  </div>
                  {canHaveChildren && (
                    <>
                      <span className="text-gray-300">|</span>
                      <div className="relative" data-no-row-click="true" ref={addChildToolbarMenuRef}>
                        {allowedChildTypesForTask.length === 1 ? (
                          <button
                            type="button"
                            onClick={() => openChildCreator(allowedChildTypesForTask[0])}
                            className="flex items-center gap-1 px-2 py-1 rounded text-[12px] font-medium text-brand hover:bg-brand-subtle transition-colors"
                            title={t('taskDetails.addChildType', { type: workItemTypeLabel(allowedChildTypesForTask[0], t) })}
                          >
                            <FiPlus className="w-3.5 h-3.5" />
                            <span>{t('taskDetails.addChild')}</span>
                            {childCount > 0 && (
                              <span className="ml-0.5 px-1 rounded-full text-[10px] font-semibold bg-brand-subtle text-blue-700">
                                {childCount}
                              </span>
                            )}
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => setAddChildToolbarMenuOpen((o) => !o)}
                              className="flex items-center gap-1 px-2 py-1 rounded text-[12px] font-medium text-brand hover:bg-brand-subtle transition-colors"
                              title={t('taskDetails.addChildWorkItem')}
                            >
                              <FiPlus className="w-3.5 h-3.5" />
                              <span>{t('taskDetails.addChild')}</span>
                              {childCount > 0 && (
                                <span className="ml-0.5 px-1 rounded-full text-[10px] font-semibold bg-brand-subtle text-blue-700">
                                  {childCount}
                                </span>
                              )}
                              <FiChevronDown className="w-3 h-3 opacity-70" />
                            </button>
                            {addChildToolbarMenuOpen && (
                              <div className="absolute left-0 top-full mt-1 z-[200] min-w-[180px] bg-surface border border-border rounded-lg shadow-xl py-1">
                                {allowedChildTypesForTask.map((childType) => (
                                  <button
                                    key={childType}
                                    type="button"
                                    className="w-full text-left px-3 py-2 text-[12px] hover:bg-brand-subtle flex items-center gap-2"
                                    onClick={() => {
                                      setAddChildToolbarMenuOpen(false);
                                      openChildCreator(childType);
                                    }}
                                  >
                                    <span className={`w-5 h-5 rounded-[2px] flex items-center justify-center ${chip(childType).bg} ${chip(childType).text}`}>
                                      {getTypeIcon(childType)}
                                    </span>
                                    {t('taskDetails.addChildType', { type: workItemTypeLabel(childType, t) })}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                  <span className="text-gray-300">|</span>
                  {/* Watch button + watcher avatars */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handleToggleWatch}
                      disabled={isTogglingWatch}
                      title={isWatching ? t('taskDetails.unwatchHint') : t('taskDetails.watchHint')}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[12px] font-medium transition-colors ${
                        isWatching
                          ? 'bg-brand-subtle text-blue-600 hover:bg-brand-subtle'
                          : 'text-text-muted hover:bg-surface-hover'
                      }`}
                    >
                      {isWatching ? <FiEye className="w-3.5 h-3.5" /> : <FiEyeOff className="w-3.5 h-3.5" />}
                      <span>{isWatching ? t('taskDetails.watching') : t('taskDetails.watch')}</span>
                      {watchers.length > 0 && (
                        <span className={`ml-0.5 px-1 rounded-full text-[10px] font-semibold ${isWatching ? 'bg-brand-subtle text-blue-700' : 'bg-surface-active text-text-secondary'}`}>
                          {watchers.length}
                        </span>
                      )}
                    </button>
                    {watchers.length > 0 && (
                      <div className="flex items-center -space-x-1.5">
                        {watchers.slice(0, 4).map((w) => (
                          <div key={w.id} title={`${w.firstName} ${w.lastName}`} className="w-6 h-6 rounded-full ring-1 ring-white overflow-hidden">
                            <Avatar name={`${w.firstName} ${w.lastName}`} size="sm" />
                          </div>
                        ))}
                        {watchers.length > 4 && (
                          <div className="w-6 h-6 rounded-full bg-surface-active ring-1 ring-white flex items-center justify-center text-[10px] font-semibold text-text-secondary">
                            +{watchers.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleSave(false)}
                    disabled={!isDirty || isSaving || !canEdit}
                    className={actionBtnOutline}
                  >
                    <FiSave className={actionBtnIconClass} aria-hidden />
                    {isSaving ? t('taskDetails.saving') : t('common.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSave(true)}
                    disabled={!isDirty || isSaving || !canEdit}
                    className={actionBtnPrimary}
                  >
                    <FiCheckCircle className={actionBtnIconClass} aria-hidden />
                    {isSaving ? t('taskDetails.saving') : !canEdit ? t('taskDetails.readOnly') : t('taskDetails.saveAndClose')}
                  </button>
                </div>
              </div>
            </div>

            {/* ============================================================
                METADATA ROW
                ============================================================ */}
            <div className="flex items-end gap-8 px-6 py-2 border-b border-border bg-bg flex-shrink-0">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">{t('workItemCreator.state')}</span>
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${STATUS_COLORS[form.status] || 'bg-gray-400'}`} />
                  <div className="w-[150px] flex-shrink-0">
                    <SearchableDropdown
                      value={form.status}
                      options={statusOptions}
                      onChange={(val) => setForm(p => ({ ...p, status: val as TaskStatus }))}
                      disabled={!canEdit}
                      placeholder={t(`taskStatus.${form.status}`, { defaultValue: form.status })}
                      searchPlaceholder={t('taskDetails.searchStatus')}
                      menuTitle={t('taskDetails.selectState')}
                      className="!py-1.5 !pl-2.5 font-medium cursor-pointer"
                      dropdownClassName="w-max min-w-[160px] max-w-[210px]"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">{t('backlogPage.columns.priority')}</span>
                <div className="w-[170px] flex-shrink-0">
                  <SearchableDropdown
                    value={form.priority}
                    options={priorityOptions}
                    onChange={(val) => setForm(p => ({ ...p, priority: val as TaskPriority }))}
                    disabled={!canEdit}
                    placeholder={t(`workItemCreator.priority.${form.priority}`, { defaultValue: form.priority })}
                    searchPlaceholder={t('taskDetails.searchPriority')}
                    menuTitle={t('taskDetails.selectPriority')}
                    className="!py-1.5 !pl-2.5 font-medium cursor-pointer"
                    dropdownClassName="w-max min-w-[160px] max-w-[210px]"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">{t('taskDetails.area')}</span>
                {canEdit ? (
                  <div className="relative" ref={moveProjectRef}>
                    <button
                      onClick={() => setMoveProjectOpen(p => !p)}
                      className="text-[13px] text-text-primary hover:text-brand hover:underline cursor-pointer text-left"
                      title={t('taskDetails.moveToProject')}
                    >
                      {task?.project?.name || '—'}
                    </button>
                    {moveProjectOpen && (
                      <div className="absolute top-full left-0 mt-1 z-50 border border-border rounded-[4px] bg-surface shadow-lg min-w-[180px] overflow-hidden">
                        <div className="px-2 py-1 bg-surface-hover border-b border-border">
                          <span className="text-[10px] font-bold text-text-muted uppercase">{t('taskDetails.moveToProject')}</span>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {allProjects.map((p: any) => (
                            <div
                              key={p.id}
                              onClick={() => handleMoveProject(p.id)}
                              className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-brand-subtle transition-colors text-[12px] ${p.id === task?.projectId ? 'bg-brand-subtle text-brand font-semibold' : 'text-text-primary'}`}
                            >
                              {p.id === task?.projectId && <span className="text-brand font-bold">✓</span>}
                              <span className="truncate">{p.name}</span>
                            </div>
                          ))}
                          {allProjects.length === 0 && (
                            <p className="text-[11px] text-text-muted px-2.5 py-2 italic">{t('taskDetails.noProjectsFound')}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-[13px] text-text-primary">{task?.project?.name || '—'}</span>
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">{t('taskDetails.reporter')}</span>
                <div className="flex items-center gap-1.5">
                  {task?.reporter ? (
                    <>
                      <Avatar name={`${task.reporter.firstName} ${task.reporter.lastName}`} size="xs" />
                      <span className="text-[13px] text-text-primary">{task.reporter.firstName} {task.reporter.lastName}</span>
                    </>
                  ) : <span className="text-[13px] text-text-muted">—</span>}
                </div>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">{t('taskDetails.created')}</span>
                <span className="text-[13px] text-text-primary">
                  {task?.createdAt ? formatDateShort(task.createdAt) : '—'}
                </span>
              </div>
            </div>

            {/* ============================================================
                SCROLLABLE CONTENT: 3-Column Layout
                ============================================================ */}
            <div className="flex-1 flex overflow-hidden relative" ref={containerRef}>

              {/* ══════ COLUMN 1: Description ══════ */}
              {colVisible[0] ? (
                <div style={{ flex: `${colPcts[0]} 0 0` }} className="relative flex flex-col overflow-hidden min-w-0">
                  <div className="flex items-center justify-between px-4 py-1.5 bg-surface-hover border-b border-border flex-shrink-0">
                    <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider">{t('workItemCreator.description')}</h3>
                    <button onClick={() => toggleColumn(0)} className="p-0.5 text-text-muted hover:text-text-primary hover:bg-surface-active rounded transition-colors" title={t('taskDetails.collapsePanel')}>
                      <FiChevronLeft className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div
                    ref={descriptionScrollRef}
                    onScroll={syncDescriptionScrollFab}
                    className="flex-1 overflow-y-auto px-4 py-3 space-y-4"
                  >

                  {/* TITLE */}
                  <section>
                    <div className="flex items-center justify-between mb-1.5">
                      <h3 className="text-[13px] font-semibold text-text-primary">{t('workItemCreator.title')}</h3>
                      <button
                        onClick={() => copyToClipboard(form.title, 'Title')}
                        className="p-1 text-text-muted hover:text-text-primary hover:bg-surface-hover rounded transition-colors"
                        title={t('taskDetails.copyTitle')}
                      >
                        <FiCopy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                      disabled={!canEdit}
                      placeholder={t('taskDetails.titlePlaceholder')}
                      className={`${fieldCls} disabled:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60`}
                    />
                  </section>

                  {/* DESCRIPTION */}
                  <section>
                    <div className="flex items-center justify-between mb-1.5">
                      <h3 className="text-[13px] font-semibold text-text-primary">{t('workItemCreator.description')}</h3>
                      <div className="relative">
                        <button
                          onClick={() => setShowAiMenu(!showAiMenu)}
                          disabled={!canEdit}
                          className="flex items-center gap-1.5 text-[12px] text-purple-600 hover:text-purple-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <FiZap className="w-3 h-3" />
                          <span>{t('taskDetails.aiTools')}</span>
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] leading-none font-semibold bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 text-white border border-blue-700">
                            AI
                          </span>
                        </button>
                        {showAiMenu && (
                          <div className="absolute right-0 top-6 bg-surface border border-border rounded shadow-lg z-20 py-1 w-52">
                            <button
                              onClick={() => handleAiRewrite('smart')}
                              disabled={isRewriting}
                              className="w-full text-left px-3 py-1.5 text-[12px] font-semibold text-white bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 hover:from-blue-700 hover:via-blue-800 hover:to-indigo-700 disabled:opacity-40 animate-pulse"
                            >
                              ⚡ Smart polish
                            </button>
                            <button onClick={() => handleAiRewrite('professional')} disabled={isRewriting} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.makeProfessional')}</button>
                            <button onClick={() => handleAiRewrite('shorter')} disabled={isRewriting} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.makeShorter')}</button>
                            <button onClick={() => handleAiRewrite('grammar')} disabled={isRewriting} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.fixGrammar')}</button>
                            <button onClick={() => handleAiRewrite('expand')} disabled={isRewriting} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.expand')}</button>
                            <button onClick={handleExpandBullets} disabled={isExpanding} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.expandBullets')}</button>
                            <button onClick={handleGenerateDesc} disabled={isGeneratingDesc} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.generateFromTitle')}</button>
                          </div>
                        )}
                      </div>
                    </div>
                    <RichTextArea
                      value={form.description}
                      onChange={(v) => setForm(p => ({ ...p, description: v }))}
                      disabled={!canEdit}
                      placeholder={t('taskDetails.descriptionPlaceholder')}
                      rows={6}
                      className={`${fieldCls} disabled:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60`}
                    />
                  </section>

                  {/* ACCEPTANCE CRITERIA */}
                  <section>
                    <div className="flex items-center justify-between mb-1.5">
                      <h3 className="text-[13px] font-semibold text-text-primary">{t('workItemCreator.acceptanceCriteria')}</h3>
                      <div className="relative">
                        <button
                          onClick={() => setShowAcMenu(!showAcMenu)}
                          disabled={!canEdit}
                          className="flex items-center gap-1.5 text-[12px] text-purple-600 hover:text-purple-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <FiZap className="w-3 h-3" />
                          <span>{t('taskDetails.aiTools')}</span>
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] leading-none font-semibold bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 text-white border border-blue-700">
                            AI
                          </span>
                        </button>
                        {showAcMenu && (
                          <div className="absolute right-0 top-6 bg-surface border border-border rounded shadow-lg z-20 py-1 w-52">
                            <button
                              onClick={() => handleAiRewriteField('acceptanceCriteria', 'smart', () => setShowAcMenu(false))}
                              disabled={isRewriting}
                              className="w-full text-left px-3 py-1.5 text-[12px] font-semibold text-white bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 hover:from-blue-700 hover:via-blue-800 hover:to-indigo-700 disabled:opacity-40 animate-pulse"
                            >
                              ⚡ Smart polish
                            </button>
                            <button onClick={() => handleAiRewriteField('acceptanceCriteria', 'professional', () => setShowAcMenu(false))} disabled={isRewriting} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.makeProfessional')}</button>
                            <button onClick={() => handleAiRewriteField('acceptanceCriteria', 'shorter', () => setShowAcMenu(false))} disabled={isRewriting} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.makeShorter')}</button>
                            <button onClick={() => handleAiRewriteField('acceptanceCriteria', 'grammar', () => setShowAcMenu(false))} disabled={isRewriting} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.fixGrammar')}</button>
                            <button onClick={() => handleAiRewriteField('acceptanceCriteria', 'expand', () => setShowAcMenu(false))} disabled={isRewriting} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover disabled:opacity-40">{t('taskDetails.expand')}</button>
                          </div>
                        )}
                      </div>
                    </div>
                    <RichTextArea
                      value={form.acceptanceCriteria}
                      onChange={(v) => setForm(p => ({ ...p, acceptanceCriteria: v }))}
                      placeholder={t('taskDetails.acceptanceCriteriaPlaceholder')}
                      rows={6}
                      className={fieldCls}
                    />
                  </section>

                  {/* CHILD ITEMS — primary hierarchy surface (Jira-style) */}
                  {canHaveChildren && (
                    <section className="rounded-lg border border-border bg-surface">
                      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-border bg-surface-hover">
                        <div className="flex items-center gap-2 min-w-0">
                          <h3 className="text-[13px] font-semibold text-text-primary">
                            Child items
                          </h3>
                          <span className="text-[11px] font-medium text-text-muted truncate">
                            {childLabel}
                            {childCount > 0 ? ` · ${childCount}` : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {allowedChildTypesForTask.length === 1 ? (
                            <button
                              type="button"
                              onClick={() => openChildCreator(allowedChildTypesForTask[0])}
                              className={actionBtnPrimary}
                            >
                              <FiPlus className={actionBtnIconClass} aria-hidden />
                              {t('taskDetails.addChildType', { type: workItemTypeLabel(allowedChildTypesForTask[0], t) })}
                            </button>
                          ) : (
                            <div className="relative" ref={addChildMenuRef}>
                              <button
                                type="button"
                                onClick={() => setAddChildMenuOpen((o) => !o)}
                                className={actionBtnPrimary}
                              >
                                <FiPlus className={actionBtnIconClass} aria-hidden />
                                {t('taskDetails.addChild')}
                                <FiChevronDown className={actionBtnIconClass} aria-hidden />
                              </button>
                              {addChildMenuOpen && (
                                <div className="absolute right-0 top-full mt-1 z-[200] min-w-[190px] bg-surface border border-border rounded-lg shadow-xl py-1">
                                  {allowedChildTypesForTask.map((childType) => (
                                    <button
                                      key={childType}
                                      type="button"
                                      className="w-full text-left px-3 py-2 text-[12px] hover:bg-brand-subtle flex items-center gap-2"
                                      onClick={() => {
                                        setAddChildMenuOpen(false);
                                        openChildCreator(childType);
                                      }}
                                    >
                                      <span className={`w-5 h-5 rounded-[2px] flex items-center justify-center ${chip(childType).bg} ${chip(childType).text}`}>
                                        {getTypeIcon(childType)}
                                      </span>
                                      {workItemTypeLabel(childType, t)}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="px-3 py-2.5">
                        {childCount > 0 ? (
                          <div className="divide-y divide-border border border-border rounded-md overflow-hidden">
                            {(task?.children ?? []).slice(0, 8).map((child: any) => {
                              const assigneeName = child.assignee ? getUserDisplayName(child.assignee) : '';
                              const assigneeShort = assigneeName
                                ? assigneeName.split(/\s+/).filter(Boolean)[0]
                                : '';
                              const priorityLabel = t(`taskPriority.${child.priority}`, {
                                defaultValue: PRIORITY_LABELS[child.priority] || child.priority || '—',
                              });
                              return (
                                <button
                                  key={child.id}
                                  type="button"
                                  onClick={() => navigateToTask(child.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-brand-subtle transition-colors"
                                >
                                  <span className={`w-5 h-5 rounded-[2px] flex items-center justify-center flex-shrink-0 ${chip(child.type).bg} ${chip(child.type).text}`}>
                                    {getTypeIcon(child.type)}
                                  </span>
                                  <span className="text-[12px] text-brand truncate flex-1 min-w-0">{child.title}</span>
                                  <span className="flex items-center gap-2 flex-shrink-0 text-[10px] text-text-muted">
                                    <span className="flex items-center gap-1 whitespace-nowrap">
                                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_COLORS[child.status] || 'bg-gray-400'}`} />
                                      {t(`taskStatus.${child.status}`, { defaultValue: child.status })}
                                    </span>
                                    <span className="whitespace-nowrap" title={priorityLabel}>
                                      {priorityLabel.replace(/^\d+\s*-\s*/, '')}
                                    </span>
                                    {child.assignee ? (
                                      <span className="whitespace-nowrap max-w-[5.5rem] truncate font-medium text-text-secondary" title={assigneeName}>
                                        {assigneeShort}
                                      </span>
                                    ) : (
                                      <span className="whitespace-nowrap text-text-muted/70">
                                        {t('taskDetails.unassigned')}
                                      </span>
                                    )}
                                  </span>
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => toggleColumn(2)}
                              className="w-full px-3 py-2 text-[12px] text-brand hover:bg-brand-subtle text-left font-medium"
                            >
                              {t('taskDetails.viewAllChildren', { count: childCount, defaultValue: `View all ${childCount} in panel →` })}
                            </button>
                          </div>
                        ) : (
                          <div className="rounded-md border border-dashed border-border bg-bg px-4 py-5 text-center">
                            <p className="text-[12px] text-text-muted mb-2">
                              {t('taskDetails.noChildItemsPrompt', { types: childLabel.toLowerCase() })}
                            </p>
                            <button
                              type="button"
                              onClick={() => openChildCreator()}
                              className={actionBtnPrimary}
                            >
                              <FiPlus className={actionBtnIconClass} aria-hidden />
                              {t('taskDetails.createFirstChild', {
                                type: workItemTypeLabel(allowedChildTypesForTask[0], t).toLowerCase(),
                              })}
                            </button>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {/* TYPE-SPECIFIC FIELDS */}
                  {task?.type === WorkItemType.EPIC && (
                    <section>
                      <h3 className="text-[13px] font-semibold text-text-primary mb-2">{t('workItemCreator.epicPlanning')}</h3>
                      <div className="space-y-2">
                        <Field label={t('workItemCreator.businessValue')}>
                          <textarea value={form.businessValue} onChange={(e) => setForm(p => ({ ...p, businessValue: e.target.value }))} placeholder={t('workItemCreator.businessValueEpicPlaceholder')} rows={2} className={fieldCls} />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label={t('workItemCreator.valueArea')}>
                            <select value={form.epicValueArea} onChange={(e) => setForm(p => ({ ...p, epicValueArea: e.target.value }))} className={fieldCls}>
                              <option value="">{t('taskDetails.select')}</option>
                              <option value="Business">{t('workItemCreator.valueAreas.business')}</option>
                              <option value="Architectural">{t('workItemCreator.valueAreas.architectural')}</option>
                            </select>
                          </Field>
                          <Field label={t('workItemCreator.risk')}>
                            <select value={form.epicRisk} onChange={(e) => setForm(p => ({ ...p, epicRisk: e.target.value }))} className={fieldCls}>
                              <option value="">{t('taskDetails.select')}</option>
                              <option value="LOW">{t('workItemCreator.riskLevels.low')}</option>
                              <option value="MEDIUM">{t('workItemCreator.riskLevels.medium')}</option>
                              <option value="HIGH">{t('workItemCreator.riskLevels.high')}</option>
                              <option value="CRITICAL">{t('workItemCreator.riskLevels.critical')}</option>
                            </select>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label={t('workItemCreator.timeCriticality')}>
                            <input type="number" min="1" max="10" value={form.epicTimeCriticality} onChange={(e) => setForm(p => ({ ...p, epicTimeCriticality: e.target.value }))} placeholder={t('workItemCreator.timeCriticalityPlaceholder')} className={fieldCls} />
                          </Field>
                          <Field label={t('workItemCreator.effortStoryPoints')}>
                            <select value={form.epicEffort} onChange={(e) => setForm(p => ({ ...p, epicEffort: e.target.value }))} className={fieldCls}>
                              <option value="">{t('taskDetails.notEstimated')}</option>
                              {['1','2','3','5','8','13','21','40','100'].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                          </Field>
                        </div>
                      </div>
                    </section>
                  )}

                  {task?.type === WorkItemType.FEATURE && (
                    <section>
                      <h3 className="text-[13px] font-semibold text-text-primary mb-2">{t('workItemCreator.featurePlanning')}</h3>
                      <div className="space-y-2">
                        <Field label={t('workItemCreator.businessValue')}>
                          <textarea value={form.businessValue} onChange={(e) => setForm(p => ({ ...p, businessValue: e.target.value }))} placeholder={t('taskDetails.businessValueFeaturePlaceholder')} rows={2} className={fieldCls} />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label={t('workItemCreator.targetRelease')}>
                            <input type="text" value={form.targetRelease} onChange={(e) => setForm(p => ({ ...p, targetRelease: e.target.value }))} placeholder={t('taskDetails.targetReleasePlaceholder')} className={fieldCls} />
                          </Field>
                          <Field label={t('workItemCreator.riskLevel')}>
                            <select value={form.riskAssessment} onChange={(e) => setForm(p => ({ ...p, riskAssessment: e.target.value }))} className={fieldCls}>
                              <option value="">{t('taskDetails.select')}</option>
                              <option value="LOW">{t('workItemCreator.riskLevels.low')}</option>
                              <option value="MEDIUM">{t('workItemCreator.riskLevels.medium')}</option>
                              <option value="HIGH">{t('workItemCreator.riskLevels.high')}</option>
                              <option value="CRITICAL">{t('workItemCreator.riskLevels.critical')}</option>
                            </select>
                          </Field>
                        </div>
                      </div>
                    </section>
                  )}

                  {task?.type === WorkItemType.USER_STORY && (
                    <section>
                      <h3 className="text-[13px] font-semibold text-text-primary mb-2">{t('workItemCreator.userStoryDetails')}</h3>
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                          <Field label={t('workItemCreator.userPersona')}>
                            <input type="text" value={form.persona} onChange={(e) => setForm(p => ({ ...p, persona: e.target.value }))} placeholder={t('taskDetails.personaPlaceholder')} className={fieldCls} />
                          </Field>
                          <Field label={t('backlogPage.columns.storyPoints')}>
                            <select value={form.storyPoints} onChange={(e) => setForm(p => ({ ...p, storyPoints: e.target.value }))} className={fieldCls}>
                              <option value="">{t('taskDetails.notEstimated')}</option>
                              {['1','2','3','5','8','13','21'].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                          </Field>
                        </div>
                      </div>
                    </section>
                  )}

                  {task?.type === WorkItemType.BUG && (
                    <section>
                      <h3 className="text-[13px] font-semibold text-text-primary mb-2">{t('workItemCreator.bugDetails')}</h3>
                      <div className="space-y-2">
                        <Field label={t('workItemCreator.stepsToReproduce')}>
                          <textarea value={form.stepsToReproduce} onChange={(e) => setForm(p => ({ ...p, stepsToReproduce: e.target.value }))} placeholder={t('workItemCreator.stepsToReproducePlaceholder')} rows={3} className={`${fieldCls} font-mono`} />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label={t('workItemCreator.expectedBehavior')}>
                            <textarea value={form.expectedBehavior} onChange={(e) => setForm(p => ({ ...p, expectedBehavior: e.target.value }))} placeholder={t('workItemCreator.expectedBehaviorPlaceholder')} rows={2} className={fieldCls} />
                          </Field>
                          <Field label={t('workItemCreator.actualBehavior')}>
                            <textarea value={form.actualBehavior} onChange={(e) => setForm(p => ({ ...p, actualBehavior: e.target.value }))} placeholder={t('workItemCreator.actualBehaviorPlaceholder')} rows={2} className={fieldCls} />
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label={t('workItemCreator.environment')}>
                            <input type="text" value={form.environment} onChange={(e) => setForm(p => ({ ...p, environment: e.target.value }))} placeholder={t('taskDetails.environmentDetailsPlaceholder')} className={fieldCls} />
                          </Field>
                          <Field label={t('workItemCreator.severity')}>
                            <select value={form.severity} onChange={(e) => setForm(p => ({ ...p, severity: e.target.value }))} className={fieldCls}>
                              <option value="">{t('taskDetails.select')}</option>
                              <option value="CRITICAL">{t('workItemCreator.riskLevels.critical')}</option>
                              <option value="HIGH">{t('workItemCreator.riskLevels.high')}</option>
                              <option value="MEDIUM">{t('workItemCreator.riskLevels.medium')}</option>
                              <option value="LOW">{t('workItemCreator.riskLevels.low')}</option>
                            </select>
                          </Field>
                        </div>
                      </div>
                    </section>
                  )}

                  {task?.type === WorkItemType.TASK && (
                    <section>
                      <h3 className="text-[13px] font-semibold text-text-primary mb-2">{t('workItemCreator.taskDetails')}</h3>
                      <div className="space-y-2">
                        <Field label={t('workItemCreator.technicalNotes')}>
                          <textarea value={form.technicalDetails} onChange={(e) => setForm(p => ({ ...p, technicalDetails: e.target.value }))} placeholder={t('taskDetails.technicalDetailsPlaceholder')} rows={3} className={`${fieldCls} font-mono`} />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label={t('workItemCreator.complexity')}>
                            <select value={form.complexity} onChange={(e) => setForm(p => ({ ...p, complexity: e.target.value }))} className={fieldCls}>
                              <option value="">{t('taskDetails.select')}</option>
                              <option value="SIMPLE">{t('workItemCreator.complexities.simple')}</option>
                              <option value="MODERATE">{t('workItemCreator.complexities.moderate')}</option>
                              <option value="COMPLEX">{t('workItemCreator.complexities.complex')}</option>
                              <option value="VERY_COMPLEX">{t('taskDetails.veryComplex')}</option>
                            </select>
                          </Field>
                          <Field label={t('taskDetails.blockers')}>
                            <input type="text" value={form.blockers} onChange={(e) => setForm(p => ({ ...p, blockers: e.target.value }))} placeholder={t('taskDetails.blockersPlaceholder')} className={fieldCls} />
                          </Field>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* DEPENDENCIES */}
                  <section>
                    <h3 className="text-[13px] font-semibold text-text-primary mb-2">{t('taskDetails.dependencies')}</h3>
                    {(taskDependencies as any[]).length > 0 && (
                      <div className="space-y-1 mb-2">
                        {(taskDependencies as any[]).map((dep: any) => {
                          const isFrom = dep.fromTaskId === activeTaskId;
                          const other = isFrom ? dep.toTask : dep.fromTask;
                          const label = dep.type === 'BLOCKS' ? (isFrom ? t('taskDetails.blocks') : t('taskDetails.blockedBy'))
                            : dep.type === 'BLOCKED_BY' ? (isFrom ? t('taskDetails.blockedBy') : t('taskDetails.blocks'))
                            : dep.type === 'RELATES_TO' ? t('taskDetails.relatesTo')
                            : t('taskDetails.duplicates');
                          return (
                            <div key={dep.id} className="flex items-center justify-between bg-surface-hover rounded p-1.5 border border-border text-[12px]">
                              <span className="text-text-muted mr-2 font-medium w-20 shrink-0">{label}</span>
                              <span className="flex-1 truncate text-text-primary">{other?.title || dep.toTaskId}</span>
                              <button onClick={() => deleteDependency(dep.id)} disabled={isDeletingDep} className="ml-2 p-0.5 text-red-500 hover:bg-red-50 rounded disabled:opacity-40"><FiX className="w-3 h-3" /></button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex gap-1">
                      <select value={depType} onChange={e => setDepType(e.target.value)} className="text-[12px] border border-border rounded-[3px] px-1.5 py-1 bg-surface focus:outline-none focus:border-brand w-28 shrink-0">
                        <option value="BLOCKS">{t('taskDetails.blocks')}</option>
                        <option value="BLOCKED_BY">{t('taskDetails.blockedBy')}</option>
                        <option value="RELATES_TO">{t('taskDetails.relatesTo')}</option>
                        <option value="DUPLICATES">{t('taskDetails.duplicates')}</option>
                      </select>
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={depTargetSearch}
                          onChange={e => { setDepTargetSearch(e.target.value); setDepSearchOpen(true); }}
                          onFocus={() => setDepSearchOpen(true)}
                          placeholder={t('taskDetails.searchTaskToLink')}
                          className="w-full text-[12px] border border-border rounded-[3px] px-2 py-1 focus:outline-none focus:border-brand"
                        />
                        {depSearchOpen && depTargetSearch.length >= 2 && (() => {
                          const allTasks: any[] = (eligibleParentsData as any)?.tasks || (Array.isArray(eligibleParentsData) ? eligibleParentsData : []);
                          const filtered = allTasks.filter(t => t.id !== activeTaskId && t.title.toLowerCase().includes(depTargetSearch.toLowerCase())).slice(0, 8);
                          return filtered.length > 0 ? (
                            <div className="absolute z-50 top-full left-0 right-0 bg-surface border border-border rounded-[3px] shadow-md mt-0.5 max-h-40 overflow-y-auto">
                              {filtered.map(t => (
                                <button
                                  key={t.id}
                                  onClick={async () => {
                                    setDepSearchOpen(false);
                                    setDepTargetSearch('');
                                    try {
                                      await createDependency({ fromTaskId: activeTaskId!, toTaskId: t.id, type: depType }).unwrap();
                                      toast.success(t('taskDetails.toasts.dependencyAdded'));
                                    } catch { toast.error(t('taskDetails.toasts.dependencyAddFailed')); }
                                  }}
                                  className="w-full text-left px-2 py-1.5 text-[12px] hover:bg-brand-subtle border-b border-[#f0f0f0] last:border-0"
                                >
                                  {t.title}
                                </button>
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </section>

                  {/* ATTACHMENTS */}
                  <section>
                    {(() => {
                      const descAttachCount = Array.isArray(task?.attachments) ? (task.attachments as any[]).length : 0;
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => setDescAttachmentsOpen((o) => !o)}
                            className="mb-1.5 flex w-full items-center gap-1.5 rounded px-0 py-0.5 text-left hover:bg-surface-hover/80 transition-colors"
                            title={descAttachmentsOpen ? t('taskDetails.collapseAttachments') : t('taskDetails.expandAttachments')}
                          >
                            {descAttachmentsOpen ? (
                              <FiChevronDown className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                            ) : (
                              <FiChevronRight className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                            )}
                            <span className="text-[13px] font-semibold text-text-primary">{t('taskDetails.attachments')}</span>
                            {descAttachCount > 0 && (
                              <span className="text-[12px] font-medium text-text-muted">({descAttachCount})</span>
                            )}
                          </button>
                          {descAttachmentsOpen && (
                            <>
                              {task?.attachments && (task.attachments as any[]).length > 0 && (
                                <div className="space-y-1 mb-2">
                                  {(task.attachments as any[]).map((url: string, index: number) => {
                                    const filename = getFileName(url);
                                    const ext = getFileExtension(filename);
                                    return (
                                      <div key={index} className="flex items-center justify-between bg-surface-hover rounded p-1.5 border border-border">
                                        <button
                                          type="button"
                                          onClick={() => setAttachmentPreview({ url, name: filename })}
                                          className="flex items-center gap-2 flex-1 min-w-0 text-left rounded px-0.5 py-0.5 -mx-0.5 hover:bg-brand-subtle/80 transition-colors"
                                          title={t('taskDetails.viewInApp')}
                                        >
                                          <FiFile className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                          <span className="text-[12px] font-medium text-text-primary truncate">{filename}</span>
                                          <span className="text-[10px] text-text-muted uppercase shrink-0">{ext || 'file'}</span>
                                        </button>
                                        <div className="flex items-center gap-0.5 shrink-0">
                                          <button
                                            type="button"
                                            onClick={() => setAttachmentPreview({ url, name: filename })}
                                            className="p-1 text-blue-600 hover:bg-brand-subtle rounded disabled:opacity-40"
                                            title={t('taskDetails.viewInApp')}
                                          >
                                            <FiEye className="w-3 h-3" />
                                          </button>
                                          <button type="button" onClick={() => handleDeleteAttachment(url)} disabled={isDeleting} className="p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-40" title={t('common.delete')}><FiTrash2 className="w-3 h-3" /></button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} className="hidden" id="detail-file-upload" accept={TASK_ATTACHMENT_ACCEPT} />
                              <label htmlFor="detail-file-upload" className="block cursor-pointer">
                                <div className="border-2 border-dashed border-border rounded p-2.5 hover:border-brand hover:bg-brand-subtle/30 transition-all text-center">
                                  <FiUpload className="w-4 h-4 text-text-muted mx-auto mb-0.5" />
                                  <p className="text-[11px] text-text-muted"><span className="text-brand font-medium">{t('taskDetails.clickToUpload')}</span> {t('taskDetails.dragAndDrop')}</p>
                                </div>
                              </label>
                              {selectedFiles.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-medium text-text-secondary">{selectedFiles.length} file(s) selected</span>
                                    <button type="button" onClick={handleUploadFiles} disabled={isUploading} className="px-2.5 py-1 text-[11px] font-medium bg-brand text-white rounded-[3px] hover:bg-brand-hover disabled:bg-surface-active disabled:text-text-muted">
                                      {isUploading ? t('taskDetails.uploading') : t('taskDetails.upload')}
                                    </button>
                                  </div>
                                  {selectedFiles.map((file, i) => (
                                    <div key={i} className="flex items-center justify-between bg-brand-subtle rounded p-1 border border-brand">
                                      <div className="flex items-center gap-1 flex-1 min-w-0">
                                        <FiFile className="w-3 h-3 text-blue-600 flex-shrink-0" />
                                        <span className="text-[11px] text-text-primary truncate">{file.name}</span>
                                      </div>
                                      <button type="button" onClick={() => handleRemoveSelectedFile(i)} className="p-0.5 text-red-500 hover:bg-red-100 rounded"><FiX className="w-3 h-3" /></button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </section>

                  {/* DISCUSSION */}
                  <section>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-[13px] font-semibold text-text-primary">{t('taskDetails.discussion')}</h3>
                      {comments.length >= 3 && (
                        <button onClick={handleSummarizeComments} disabled={isSummarizingComments} className="text-[12px] text-purple-600 hover:text-purple-800 flex items-center gap-1 font-medium disabled:opacity-40">
                          <FiZap className="w-3 h-3" />{isSummarizingComments ? t('taskDetails.summarizing') : t('taskDetails.aiSummary')}
                        </button>
                      )}
                    </div>

                    {commentSummary && (
                      <div className="bg-brand-subtle border border-brand rounded p-3 mb-3">
                        <div className="flex items-start gap-2">
                          <FiZap className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-[12px] font-semibold text-blue-900 mb-1">{t('taskDetails.aiSummary')}</p>
                            <p className="text-[12px] text-blue-800">{commentSummary.summary}</p>
                          </div>
                          <button onClick={() => setCommentSummary(null)} className="text-blue-600 hover:text-blue-800 text-sm leading-none">&times;</button>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-2 mb-3">
                      <Avatar name={getUserDisplayName(user)} className="mt-0.5" />
                      <div className="flex-1 flex gap-2 relative">
                        <div className="flex-1 relative">
                          <input
                            ref={commentInputRef}
                            type="text"
                            value={commentText}
                            onChange={handleCommentChange}
                            placeholder={t('taskDetails.commentPlaceholder')}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') { setMentionQuery(null); return; }
                              if (e.key === 'Enter' && !e.shiftKey && !mentionQuery) { e.preventDefault(); handleAddComment(); }
                            }}
                            className="w-full px-3 py-1.5 border border-border rounded-[3px] text-[13px] bg-surface hover:border-brand focus:border-brand focus:outline-none transition-colors"
                          />
                          {/* @mention dropdown */}
                          {mentionQuery !== null && mentionDropdownUsers.length > 0 && (
                            <div className="absolute bottom-full left-0 mb-1 w-56 bg-surface border border-border rounded-lg shadow-xl z-[300] overflow-hidden">
                              <div className="px-3 py-1.5 text-[11px] font-semibold text-text-muted border-b border-border">{t('taskDetails.mentionTeamMember')}</div>
                              {mentionDropdownUsers.map((u: any) => (
                                <button
                                  key={u.id}
                                  type="button"
                                  onMouseDown={(e) => { e.preventDefault(); handleMentionSelect(u); }}
                                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-indigo-50 text-left"
                                >
                                  <Avatar name={`${u.firstName} ${u.lastName}`} size="sm" />
                                  <span className="text-[12px] text-text-primary">{u.firstName} {u.lastName}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={handleAddComment}
                          disabled={!commentText.trim() || isAddingComment}
                          className="px-3 py-1.5 text-[12px] font-medium bg-brand text-white rounded-[3px] hover:bg-brand-hover disabled:bg-surface-active disabled:text-text-muted transition-colors"
                        >
                          {isAddingComment ? '…' : 'Add'}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {comments.length === 0 ? (
                        <p className="text-[12px] text-text-muted italic">{t('taskDetails.noComments')}</p>
                      ) : (
                        comments.map((comment: any) => (
                          <div key={comment.id} className="flex items-start gap-2">
                            <Avatar name={getUserDisplayName(comment.user)} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[12px] font-semibold text-text-primary">{getUserDisplayName(comment.user)}</span>
                                <span className="text-[10px] text-text-muted">{new Date(comment.createdAt).toLocaleString()}</span>
                              </div>
                              <p className="text-[12px] text-text-secondary mt-0.5">{comment.content}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </section>
                </div>
                  {descScrollHasOverflow && (
                    <button
                      type="button"
                      onClick={() => {
                        const el = descriptionScrollRef.current;
                        if (!el) return;
                        el.scrollTo({ top: descScrollFabToTop ? 0 : el.scrollHeight, behavior: 'smooth' });
                        window.setTimeout(() => syncDescriptionScrollFab(), 400);
                      }}
                      className="absolute bottom-3 right-2 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-border bg-surface/95 text-text-secondary shadow-md hover:bg-surface-hover hover:text-text-primary"
                      title={descScrollFabToTop ? t('taskDetails.scrollToTop') : t('taskDetails.scrollToBottom')}
                    >
                      {descScrollFabToTop ? <FiArrowUp className="w-4 h-4" /> : <FiArrowDown className="w-4 h-4" />}
                    </button>
                  )}
              </div>
              ) : (
                <div
                  onClick={() => toggleColumn(0)}
                  className="w-9 flex flex-col items-center justify-center bg-surface-hover border-r border-border cursor-pointer hover:bg-surface-active transition-colors flex-shrink-0"
                  title={t('taskDetails.expandDescription')}
                >
                  <FiChevronRight className="w-3.5 h-3.5 text-text-muted mb-2" />
                  <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                    Description
                  </span>
                </div>
              )}

              {/* ── Resizer 1 ── */}
              {colVisible[0] && colVisible[1] && (
                <div
                  onMouseDown={(e) => handleDividerMouseDown(0, e)}
                  className="w-[5px] cursor-col-resize bg-transparent hover:bg-blue-400/40 active:bg-brand-subtle0/50 transition-colors flex-shrink-0 relative group"
                  title={t('taskDetails.dragToResize')}
                >
                  <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[1px] bg-border group-hover:bg-blue-400" />
                </div>
              )}

              {/* ══════ COLUMN 2: Properties ══════ */}
              {colVisible[1] ? (
                <div style={{ flex: `${colPcts[1]} 0 0` }} className="flex flex-col overflow-hidden min-w-0">
                  <div className="flex items-center justify-between px-3 py-1.5 bg-surface-hover border-b border-border flex-shrink-0">
                    <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider">{t('taskDetails.properties')}</h3>
                    <button onClick={() => toggleColumn(1)} className="p-0.5 text-text-muted hover:text-text-primary hover:bg-surface-active rounded transition-colors" title={t('taskDetails.collapsePanel')}>
                      <FiChevronLeft className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto bg-bg">

                {/* Due date, estimates, and read-only detail fields — single stack (no section headers) */}
                <div className="px-3 py-2.5 border-b border-border space-y-0.5">
                  <SidebarRow label="Due Date">
                    <div className="flex flex-col gap-1">
                      <input type="date" value={form.dueDate} onChange={(e) => setForm(p => ({ ...p, dueDate: e.target.value }))} className={`${sidebarCompactCls} w-full`} />
                      <span className="text-[10px] text-text-muted">{browserTimeZone}</span>
                    </div>
                  </SidebarRow>
                  {(task?.type === WorkItemType.TASK || task?.type === WorkItemType.BUG) && (
                    <SidebarRow label="Est. Hours">
                      <input type="number" min="0" step="0.5" value={form.estimatedHours} onChange={(e) => setForm(p => ({ ...p, estimatedHours: e.target.value }))} placeholder="—" className={`${sidebarCompactCls} w-full`} />
                    </SidebarRow>
                  )}
                  <SidebarRow label="Priority">
                    <span className="text-[12px] text-text-primary">{t(`workItemCreator.priority.${form.priority}`, { defaultValue: form.priority })}</span>
                  </SidebarRow>
                  <SidebarRow label="Tags">
                    <input type="text" value={form.tags} onChange={(e) => setForm(p => ({ ...p, tags: e.target.value }))} placeholder={t('workItemCreator.tagsPlaceholder')} className={`${sidebarCompactCls} w-full`} />
                  </SidebarRow>
                  {task?.type === WorkItemType.FEATURE && (
                    <>
                      <SidebarRow label={t('workItemCreator.businessValue')}><span className="text-[12px] text-text-primary">{form.businessValue || '—'}</span></SidebarRow>
                      <SidebarRow label={t('workItemCreator.riskLevel')}><span className="text-[12px] text-text-primary">{form.riskAssessment || '—'}</span></SidebarRow>
                    </>
                  )}
                  {task?.type === WorkItemType.USER_STORY && (
                    <>
                      <SidebarRow label={t('backlogPage.columns.storyPoints')}><span className="text-[12px] text-text-primary">{form.storyPoints || '—'}</span></SidebarRow>
                      <SidebarRow label={t('workItemCreator.userPersona')}><span className="text-[12px] text-text-primary">{form.persona || '—'}</span></SidebarRow>
                    </>
                  )}
                  {task?.type === WorkItemType.BUG && (
                    <SidebarRow label={t('workItemCreator.severity')}><span className="text-[12px] text-text-primary">{form.severity || '—'}</span></SidebarRow>
                  )}
                  {task?.type === WorkItemType.TASK && (
                    <SidebarRow label={t('workItemCreator.complexity')}><span className="text-[12px] text-text-primary">{form.complexity || '—'}</span></SidebarRow>
                  )}
                </div>

                {/* Attachments in Properties — Tasks/Bugs often collapse the Description column; keep files accessible here */}
                {(task?.type === WorkItemType.TASK || task?.type === WorkItemType.BUG) && (
                  <div className="px-3 py-2.5 border-b border-border">
                    <button
                      type="button"
                      onClick={() => setPropsAttachmentsOpen((o) => !o)}
                      className="mb-2 flex w-full items-center gap-1.5 rounded px-0 py-0.5 text-left hover:bg-surface/60 transition-colors"
                      title={propsAttachmentsOpen ? t('taskDetails.collapseAttachments') : t('taskDetails.expandAttachments')}
                    >
                      {propsAttachmentsOpen ? (
                        <FiChevronDown className="w-3 h-3 shrink-0 text-text-muted" />
                      ) : (
                        <FiChevronRight className="w-3 h-3 shrink-0 text-text-muted" />
                      )}
                      <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider">{t('taskDetails.attachments')}</span>
                      {Array.isArray(task?.attachments) && (task.attachments as any[]).length > 0 && (
                        <span className="text-[10px] font-semibold text-text-muted">
                          ({(task.attachments as any[]).length})
                        </span>
                      )}
                    </button>
                    {propsAttachmentsOpen && (
                      <div className="space-y-1.5">
                        {task?.attachments && (task.attachments as any[]).length > 0 && (
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {(task.attachments as any[]).map((url: string, index: number) => {
                              const filename = getFileName(url);
                              const ext = getFileExtension(filename);
                              return (
                                <div key={index} className="flex items-center justify-between bg-surface rounded border border-border px-1.5 py-1">
                                  <button
                                    type="button"
                                    onClick={() => setAttachmentPreview({ url, name: filename })}
                                    className="flex items-center gap-1.5 flex-1 min-w-0 text-left rounded py-0.5 pr-1 hover:bg-brand-subtle/80 transition-colors"
                                    title={t('taskDetails.viewInApp')}
                                  >
                                    <FiFile className="w-3 h-3 text-blue-600 shrink-0" />
                                    <span className="text-[10px] font-medium text-text-primary truncate">{filename}</span>
                                    <span className="text-[9px] text-text-muted uppercase shrink-0">{ext || 'file'}</span>
                                  </button>
                                  <div className="flex items-center gap-0.5 shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => setAttachmentPreview({ url, name: filename })}
                                      className="p-0.5 text-blue-600 hover:bg-brand-subtle rounded disabled:opacity-40"
                                      title={t('taskDetails.viewInApp')}
                                    >
                                      <FiEye className="w-3 h-3" />
                                    </button>
                                    <button type="button" onClick={() => handleDeleteAttachment(url)} disabled={isDeleting} className="p-0.5 text-red-500 hover:bg-red-50 rounded disabled:opacity-40" title={t('common.delete')}><FiTrash2 className="w-3 h-3" /></button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full border border-dashed border-border rounded-[3px] p-2 hover:border-brand hover:bg-brand-subtle/30 transition-all text-center"
                        >
                          <FiUpload className="w-3.5 h-3.5 text-text-muted mx-auto mb-0.5" />
                          <p className="text-[10px] text-text-muted"><span className="text-brand font-medium">{t('taskDetails.addFiles')}</span> ({t('attachments.maxPerFile', { limit: maxAttachmentLabel })})</p>
                        </button>
                        {selectedFiles.length > 0 && (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between gap-1">
                              <span className="text-[10px] font-medium text-text-secondary">{selectedFiles.length} selected</span>
                              <button type="button" onClick={handleUploadFiles} disabled={isUploading} className="px-2 py-0.5 text-[10px] font-medium bg-brand text-white rounded-[3px] hover:bg-brand-hover disabled:bg-surface-active disabled:text-text-muted">
                                {isUploading ? '…' : 'Upload'}
                              </button>
                            </div>
                            {selectedFiles.map((file, i) => (
                              <div key={i} className="flex items-center justify-between bg-brand-subtle rounded px-1.5 py-0.5 border border-brand">
                                <span className="text-[10px] text-text-primary truncate flex-1 min-w-0">{file.name}</span>
                                <button type="button" onClick={() => handleRemoveSelectedFile(i)} className="p-0.5 text-red-500 hover:bg-red-100 rounded shrink-0"><FiX className="w-3 h-3" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* TIME TRACKING - Only for Tasks and Bugs */}
                {(task?.type === WorkItemType.TASK || task?.type === WorkItemType.BUG) && (
                  <SidebarSection title={t('taskDetails.timeTracking')}>
                    <div className="space-y-1.5">
                      {canLogTime ? (
                        <>
                          {isAdmin && (
                            <SidebarRow label={t('taskDetails.logFor')}>
                              <select
                                value={logForUserId || userId || ''}
                                onChange={(e) => setLogForUserId(e.target.value)}
                                className={`${sidebarInputCls} w-full`}
                              >
                                <option value={userId || ''}>{getUserDisplayName(user)} ({t('taskDetails.me', { defaultValue: 'me' })})</option>
                                {users
                                  .filter((u: any) => u.id !== userId)
                                  .map((u: any) => (
                                    <option key={u.id} value={u.id}>{getUserDisplayName(u)}</option>
                                  ))}
                              </select>
                            </SidebarRow>
                          )}
                          <SidebarRow label={t('taskDetails.workDate')}>
                            <input type="date" value={timeLogDate} onChange={(e) => setTimeLogDate(e.target.value)} className={`${sidebarInputCls} w-full`} />
                          </SidebarRow>
                          <SidebarRow label="Hours">
                            <input type="number" min="0.25" step="0.25" value={hoursToLog} onChange={(e) => setHoursToLog(e.target.value)} className={`${sidebarInputCls} w-full`} />
                          </SidebarRow>
                          <SidebarRow label={t('workItemCreator.description')}>
                            <input type="text" value={timeDescription} onChange={(e) => setTimeDescription(e.target.value)} placeholder={t('taskDetails.workDescriptionPlaceholder')} className={`${sidebarInputCls} w-full`} />
                          </SidebarRow>
                          <button onClick={handleLogTime} disabled={isLoggingTime} className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold rounded-[4px] bg-brand text-white hover:bg-brand-hover disabled:opacity-40 transition-colors shadow-sm">
                            <FiClock className="w-3 h-3" />
                            {isLoggingTime ? t('taskDetails.logging') : t('taskDetails.logTime')}
                          </button>
                        </>
                      ) : !canLogTimeByRole ? (
                        <p className="text-[10px] text-text-muted italic">{t('taskDetails.cannotLogTime')}</p>
                      ) : (
                        <p className="text-[10px] text-amber-700 italic">{TIME_LOG_SPRINT_REQUIRED_MESSAGE}</p>
                      )}
                      {timeEntries.length > 0 && (
                        <div className="space-y-0.5 mt-1 max-h-28 overflow-y-auto">
                          {timeEntries.slice(0, 8).map((entry: any) => (
                            <div key={entry.id} className="flex items-center justify-between py-0.5 border-b border-border last:border-0">
                              <div>
                                <div className="text-[10px] text-text-primary">{entry.user ? getUserDisplayName(entry.user) : t('taskDetails.workLog')}{entry.description ? ` — ${entry.description}` : ''}</div>
                                <div className="text-[9px] text-text-muted">{new Date(entry.startTime).toLocaleString()}</div>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] font-semibold text-brand">{((entry.duration || 0) / 60).toFixed(1)}h</span>
                                {(isAdmin || entry.userId === userId) && (
                                  <>
                                    <button onClick={() => setEditTimeEntry(entry)} className="text-indigo-500 hover:text-indigo-700 transition-colors" title={t('taskDetails.editEntry')}>
                                      <FiEdit2 className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => handleDeleteTimeEntry(entry.id)} className="text-red-400 hover:text-red-600 transition-colors" title={t('taskDetails.deleteEntry')}>
                                      <FiTrash2 className="w-3 h-3" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </SidebarSection>
                )}

                {/* CUSTOM FIELDS */}
                {customFields.length > 0 && (
                  <SidebarSection title={t('taskDetails.customFields')}>
                    <div className="space-y-1.5">
                      {customFields.map((field: any) => (
                        <div key={field.id}>
                          <label className="block text-[10px] text-text-muted mb-0.5">
                            {field.name}{field.required && <span className="text-red-500 ml-0.5">*</span>}
                          </label>
                          {field.type === 'TEXT' && <input type="text" className={sidebarInputCls} value={cfValues[field.id] || ''} onChange={(e) => setCfValues(p => ({ ...p, [field.id]: e.target.value }))} />}
                          {field.type === 'NUMBER' && <input type="number" className={sidebarInputCls} value={cfValues[field.id] || ''} onChange={(e) => setCfValues(p => ({ ...p, [field.id]: e.target.value }))} />}
                          {field.type === 'DATE' && <input type="date" className={sidebarInputCls} value={cfValues[field.id] || ''} onChange={(e) => setCfValues(p => ({ ...p, [field.id]: e.target.value }))} />}
                          {field.type === 'CHECKBOX' && (
                            <label className="flex items-center gap-1.5">
                              <input type="checkbox" checked={cfValues[field.id] === 'true'} onChange={(e) => setCfValues(p => ({ ...p, [field.id]: String(e.target.checked) }))} className="rounded border-border" />
                              <span className="text-[11px] text-text-secondary">{cfValues[field.id] === 'true' ? t('taskDetails.yes') : t('taskDetails.no')}</span>
                            </label>
                          )}
                          {field.type === 'DROPDOWN' && (
                            <select className={sidebarInputCls} value={cfValues[field.id] || ''} onChange={(e) => setCfValues(p => ({ ...p, [field.id]: e.target.value }))}>
                              <option value="">{t('taskDetails.select')}</option>
                              {field.options?.map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          )}
                          {(field.type === 'URL' || field.type === 'EMAIL') && (
                            <input type={field.type === 'URL' ? 'url' : 'email'} className={sidebarInputCls} value={cfValues[field.id] || ''} onChange={(e) => setCfValues(p => ({ ...p, [field.id]: e.target.value }))} />
                          )}
                        </div>
                      ))}
                    </div>
                  </SidebarSection>
                )}



                {/* PARENT LINK */}
                {(task?.parent || eligibleParentType) && (
                  <SidebarSection title={t('taskDetails.parent')}>
                    <div className="space-y-1.5" ref={parentPickerRef}>
                      {/* Current parent */}
                      {task?.parent ? (
                        <div className="flex items-center gap-1.5 group/parent">
                          <div
                            onClick={() => navigateToTask(task.parent.id)}
                            className="flex items-center gap-1.5 flex-1 min-w-0 p-1 rounded hover:bg-surface-hover cursor-pointer transition-colors"
                          >
                            <div className={`w-[16px] h-[16px] rounded-[2px] flex items-center justify-center flex-shrink-0 ${chip(task.parent.type).bg} ${chip(task.parent.type).text}`}>
                              {getTypeIcon(task.parent.type)}
                            </div>
                            <span className="text-[11px] text-brand hover:underline truncate">{task.parent.title}</span>
                          </div>
                          {canEdit && (
                            <button
                              onClick={() => { setParentPickerOpen(p => !p); setParentSearch(''); }}
                              className="flex-shrink-0 p-0.5 rounded text-text-muted hover:text-text-primary hover:bg-surface-active transition-colors"
                              title={t('taskDetails.changeParent')}
                            >
                              <FiGitBranch className="w-3 h-3" />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => handleParentChange(null)}
                              className="flex-shrink-0 p-0.5 rounded text-text-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                              title={t('taskDetails.removeParent')}
                            >
                              <FiX className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ) : (
                        canEdit && eligibleParentType && (
                          <button
                            onClick={() => { setParentPickerOpen(p => !p); setParentSearch(''); }}
                            className="flex items-center gap-1 text-[11px] text-brand hover:underline"
                          >
                            <FiPlus className="w-3 h-3" /> Assign parent
                          </button>
                        )
                      )}

                      {/* Parent picker dropdown */}
                      {parentPickerOpen && eligibleParentType && (
                        <div className="border border-border rounded-[4px] bg-surface shadow-md overflow-hidden">
                          <div className="flex items-center gap-1.5 px-2 py-1 border-b border-border bg-surface-hover">
                            <FiSearch className="w-3 h-3 text-text-muted flex-shrink-0" />
                            <input
                              autoFocus
                              type="text"
                              value={parentSearch}
                              onChange={(e) => setParentSearch(e.target.value)}
                              placeholder={`Search ${eligibleParentType.replace('_', ' ').toLowerCase()}s…`}
                              className="flex-1 text-[11px] bg-transparent outline-none text-text-primary"
                            />
                          </div>
                          <div className="max-h-40 overflow-y-auto">
                            {eligibleParents
                              .filter(p => !parentSearch || p.title.toLowerCase().includes(parentSearch.toLowerCase()))
                              .map((p: any) => (
                                <div
                                  key={p.id}
                                  onClick={() => handleParentChange(p.id)}
                                  className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-brand-subtle transition-colors ${p.id === task?.parentId ? 'bg-brand-subtle' : ''}`}
                                >
                                  <div className={`w-[14px] h-[14px] rounded-[2px] flex items-center justify-center flex-shrink-0 ${chip(p.type).bg} ${chip(p.type).text}`}>
                                    {getTypeIcon(p.type)}
                                  </div>
                                  <span className="text-[11px] text-text-primary truncate">{p.title}</span>
                                </div>
                              ))}
                            {eligibleParents.filter(p => !parentSearch || p.title.toLowerCase().includes(parentSearch.toLowerCase())).length === 0 && (
                              <p className="text-[11px] text-text-muted px-2 py-2 italic">{t('taskDetails.noMatchingItems')}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </SidebarSection>
                )}

                {/* ACTIVITY HISTORY */}
                <SidebarSection title={t('taskDetails.activityHistory')}>
                  <div className="max-h-96 overflow-y-auto">
                    <ActivityTimeline
                      taskId={activeTaskId as string}
                      history={taskHistoryEntries}
                      loading={isLoadingHistory}
                    />
                  </div>
                </SidebarSection>

                  </div>
                </div>
              ) : (
                <div
                  onClick={() => toggleColumn(1)}
                  className="w-9 flex flex-col items-center justify-center bg-surface-hover border-r border-border cursor-pointer hover:bg-surface-active transition-colors flex-shrink-0"
                  title={t('taskDetails.expandProperties')}
                >
                  <FiChevronRight className="w-3.5 h-3.5 text-text-muted mb-2" />
                  <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                    Properties
                  </span>
                </div>
              )}

              {/* ── Resizer 2 + COLUMN 3: Child Items (only when hierarchy allows children) ── */}
              {canHaveChildren && colVisible[1] && colVisible[2] && (
                <div
                  onMouseDown={(e) => handleDividerMouseDown(1, e)}
                  className="w-[5px] cursor-col-resize bg-transparent hover:bg-blue-400/40 active:bg-brand-subtle0/50 transition-colors flex-shrink-0 relative group"
                  title={t('taskDetails.dragToResize')}
                >
                  <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[1px] bg-border group-hover:bg-blue-400" />
                </div>
              )}

              {canHaveChildren && (colVisible[2] ? (
                <div style={{ flex: `${colPcts[2]} 0 0` }} className="flex flex-col overflow-hidden min-w-0">
                  <div className="flex items-center justify-between px-3 py-1.5 bg-surface-hover border-b border-border flex-shrink-0">
                    <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider">{childLabel}</h3>
                    <div className="flex items-center gap-1">
                      {allowedChildTypesForTask.map((childType) => {
                        const label = workItemTypeLabel(childType, t);
                        return (
                          <button
                            key={childType}
                            onClick={() => openChildCreator(childType)}
                            className="flex items-center gap-0.5 text-[10px] text-brand hover:underline font-medium"
                            title={`Add ${label}`}
                          >
                            <FiPlus className="w-3 h-3" /> {label}
                          </button>
                        );
                      })}
                      <button onClick={() => toggleColumn(2)} className="p-0.5 text-text-muted hover:text-text-primary hover:bg-surface-active rounded transition-colors ml-1" title={t('taskDetails.collapsePanel')}>
                        <FiChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-3 py-3">
                    {canHaveChildren ? (
                      task?.children && task.children.length > 0 ? (
                        <div className="border border-border rounded overflow-hidden">
                          <div className="divide-y divide-border">
                            {task.children.map((child: any) => (
                              <div 
                                key={child.id} 
                                onClick={() => navigateToTask(child.id)}
                                className="flex flex-col gap-1 px-3 py-2 hover:bg-brand-subtle transition-colors cursor-pointer"
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-5 h-5 rounded-[2px] flex items-center justify-center flex-shrink-0 ${chip(child.type).bg} ${chip(child.type).text}`}>
                                    {getTypeIcon(child.type)}
                                  </div>
                                  <span className="text-[12px] text-brand hover:underline truncate flex-1 min-w-0">{child.title}</span>
                                </div>
                                <div className="flex items-center gap-3 pl-7 text-[10px] text-text-muted">
                                  <div className="flex items-center gap-1">
                                    <div className={`w-1.5 h-1.5 rounded-full ${STATUS_COLORS[child.status] || 'bg-gray-400'}`} />
                                    <span>{t(`taskStatus.${child.status}`, { defaultValue: child.status })}</span>
                                  </div>
                                  {child.type === WorkItemType.USER_STORY && (child as any)?.metadata?.storyPoints && (
                                    <span className="font-semibold" title={t('backlogPage.columns.storyPoints')}>{(child as any).metadata.storyPoints} SP</span>
                                  )}
                                  <span>{t(`taskPriority.${child.priority}`, { defaultValue: child.priority || '—' })}</span>
                                  {child.assignee ? (
                                    <div className="flex items-center gap-1">
                                      <Avatar name={getUserDisplayName(child.assignee)} size="xs" />
                                      <span>{getUserDisplayName(child.assignee).split(' ')[0]}</span>
                                    </div>
                                  ) : (
                                    <span className="text-text-muted">{t('taskDetails.unassigned')}</span>
                                  )}
                                  {/* Show rollup hours for User Stories, direct hours for Tasks/Bugs */}
                                  {(() => {
                                    if (child.type === WorkItemType.TASK || child.type === WorkItemType.BUG) {
                                      return child.estimatedHours ? <span title={t('workItemCreator.estimatedHours')}>{child.estimatedHours}h</span> : null;
                                    } else if (child.type === WorkItemType.USER_STORY && child.children && child.children.length > 0) {
                                      const totalHours = child.children.reduce((sum: number, grandchild: any) => 
                                        sum + (grandchild.estimatedHours || 0), 0);
                                      return totalHours > 0 ? <span title={t('workItemCreator.estimatedHours')}>{totalHours}h</span> : null;
                                    }
                                    return null;
                                  })()}
                                  <span>{child.dueDate ? formatDateShort(child.dueDate) : '—'}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-[12px] text-text-muted italic">{t('taskDetails.noChildItems')}</p>
                      )
                    ) : (
                      <p className="text-[12px] text-text-muted italic">{t('taskDetails.noChildItems')}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => toggleColumn(2)}
                  className="w-9 flex flex-col items-center justify-center bg-surface-hover border-l border-border cursor-pointer hover:bg-surface-active transition-colors flex-shrink-0"
                  title={`Expand ${childLabel}`}
                >
                  <FiChevronLeft className="w-3.5 h-3.5 text-text-muted mb-2" />
                  <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                    {childLabel}
                  </span>
                </div>
              ))}

            </div>
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      {createPortal(portal, document.body)}

      {showCreator && creatorType && task && (
        <FullScreenWorkItemCreator
          isOpen={showCreator}
          onClose={() => { setShowCreator(false); setCreatorType(null); }}
          onSave={() => {
            setShowCreator(false);
            setCreatorType(null);
            toast.success(
              t('taskDetails.toasts.workItemCreated', {
                type: workItemTypeLabel(creatorType, t),
              }),
            );
            if (onSaved) onSaved();
          }}
          initialType={creatorType}
          projectId={task.projectId}
          parentId={task.id}
        />
      )}

      {attachmentPreview && (
        <AttachmentPreviewModal
          isOpen
          onClose={() => setAttachmentPreview(null)}
          fileUrl={attachmentPreview.url}
          fileName={attachmentPreview.name}
        />
      )}

      <EditTimeEntryModal
        open={Boolean(editTimeEntry)}
        onClose={() => setEditTimeEntry(null)}
        entry={editTimeEntry}
        onSaved={() => refetchTimeEntries()}
      />
    </>
  );
}

/* ================================================================
   Sub-components
   ================================================================ */

const fieldCls = 'w-full px-3 py-2 border border-border rounded-[3px] text-[13px] text-text-primary bg-surface hover:border-brand focus:border-brand focus:outline-none resize-y transition-colors';
const sidebarInputCls = 'w-full px-2 py-1 border border-border rounded-[3px] text-[11px] text-text-primary bg-surface hover:border-brand focus:border-brand focus:outline-none transition-colors';
const sidebarCompactCls = 'px-1.5 py-0.5 border border-border rounded-[3px] text-[11px] text-text-primary bg-surface hover:border-brand focus:border-brand focus:outline-none transition-colors';
const metaSelectCls = 'text-[13px] font-medium text-text-primary bg-surface border border-border rounded-md px-2.5 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-text-secondary mb-0.5">{label}</label>
      {children}
    </div>
  );
}

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="px-3 py-2.5 border-b border-border">
      <h4 className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">{title}</h4>
      {children}
    </div>
  );
}

function SidebarRow({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div className="grid grid-cols-[90px_1fr] items-center py-0.5">
      <span className="text-[11px] text-text-muted">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </div>
  );
}
